import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  IconButton, Paper, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon       from '@mui/icons-material/ArrowBack'
import VisibilityIcon      from '@mui/icons-material/Visibility'
import LoginIcon           from '@mui/icons-material/Login'
import AutoFixHighIcon     from '@mui/icons-material/AutoFixHigh'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../../supabaseClient'
import Markdown from '../../../components/Markdown'
import { startImpersonation } from '../../../auth/impersonation'
import { useAuth }  from '../../../auth/AuthContext'
import { readFunctionError } from '../../../lib/functionError'
import JournalTab from '../../step1/JournalTab'
import ConversationThread from '../../../components/messaging/ConversationThread'
import TrackSwitchPanel from '../../../components/mentor/TrackSwitchPanel'
import TrackSwitchArchivePanel from '../../../components/mentor/TrackSwitchArchivePanel'

// ── Types ─────────────────────────────────────────────────────────────────────

interface CategorySelection {
  category_id:  string
  name:         string
  reason:       string
}
interface AcceptedTopic {
  id:                  string
  title:               string
  description:         string
  decision:            'accept' | 'reject' | null
  justification:       string | null
  decided_at:          string | null
  source:              'ai' | 'student'
  finalist_thoughts:   string | null
  evaluation:          any | null
  spanning_categories: string[] | null
}
interface QAEntry {
  id:                      string
  topic_recommendation_id: string
  question:                string
  answer:                  string
  asked_at:                string
}
interface ReviewLogEntry {
  log_id:       string
  action:       'comment' | 'schedule' | 'mark_held' | 'approve' | 'message' | 'student_message' | 'cancel_meeting'
  note:         string | null
  acted_at:     string
  reviewer_id:  string | null
  reviewer_name: string | null
}

interface SubmissionVersion {
  step_number: number
  payload:     Record<string, unknown>
  created_at:  string
}

interface RevisionRequestRow {
  id:              string
  step_number:     number
  target_subphase: string | null
  reason:          string
  status:          'pending' | 'approved' | 'denied'
  mentor_note:     string | null
  requested_at:    string
  decided_at:      string | null
}

interface Step2DraftPayload {
  // Sci
  iv?:                string
  dv?:                string
  cv?:                string[]
  question?:          string
  // Eng
  user_context?:      string
  need?:              string
  constraints?:       string[]
  success_criteria?:  string[]
  problem_statement?: string
  // Both
  method_categories?: string[]
  method?:            string
}

interface Step2DraftRow {
  track_committed: 'scientific_method' | 'engineering_design' | null
  status:          'draft' | 'submitted' | 'approved_draft' | 'completed'
  mentor_note:     string | null
  draft_payload:   Step2DraftPayload | null
  updated_at:      string
  ai_review?:      Step2Review | null   // cached AI review (auto-run on submit)
  ai_review_at?:   string | null
}

interface Step3Source { source?: string; relevance?: string; note?: string }
interface Step3Paper  { url?: string; title?: string; abstract?: string }
interface Step3Payload {
  track?:                'scientific_method' | 'engineering_design'
  notebooklm_url?:       string
  notebook_access?:      { state?: string; reason?: string }
  keywords?:             string[]
  review_paper?:         Step3Paper
  model_paper?:          Step3Paper
  significance?:         string
  key_concepts?:         string
  principles?:           string
  what_research_shows?:  string
  the_gap?:              string
  conclusion?:           string
  refined_question?:     string
  purpose?:              string
  paper_draft?:          string
  feasibility?:          { iv_levels?: string; dv_measurement?: string; how_works?: string; how_build?: string }
  // legacy v1
  sources?:              Step3Source[]
  synthesis_background?: string
  synthesis_literature?: string
}
interface Step3Row {
  status:      'draft' | 'submitted' | 'approved_draft' | 'completed'
  mentor_note: string | null
  payload:     Step3Payload | null
  updated_at:  string
  mentor_section_comments?: Record<string, string> | null
  submission_note?: string | null
  ai_review?:       MentorReview | null    // cached AI review (auto-run on submit)
  ai_review_at?:    string | null
  last_sent_review?: { at: string; note: string | null; section_comments?: Record<string, string> } | null
}

interface Step4Dim   { state?: string; note?: string }
interface Step4Block {
  dv?: string; dv_kind?: string; dv_measurement?: string
  measured_quantities?: string; formula?: string
  needs_tool?: boolean; tool?: string
  hypothesis?: string; rationale?: string; supporting_background?: string
}
interface Step4EngReq { requirement?: string; criterion?: string }
interface Step4MlPrototype { dataset?: string; model_name?: string; package?: string; colab_url?: string }
interface Step4Payload {
  // Scientific
  question?: string; iv?: string; cv?: string; blocks?: Step4Block[]
  ml_prototype?: Step4MlPrototype   // data-science track only
  // Engineering
  problem?: string; for_human_user?: boolean; users_needs?: string; performance_goal?: string
  prior_art?: string; requirements?: Step4EngReq[]; mvp?: string; tools?: string; solution?: string
  ai_feedback?: Record<string, { shape?: Step4Dim; anchor?: Step4Dim }>
}
interface Step4Row {
  status:      'draft' | 'submitted' | 'approved_draft' | 'completed'
  track:       'scientific_method' | 'engineering_design'
  study_mode?: 'experimental' | 'data_science' | null
  mentor_note: string | null
  payload:     Step4Payload | null
  updated_at:  string
  mentor_section_comments?: Record<string, string> | null
  ai_review?:       MentorReview | null    // cached AI review (auto-run on submit)
  ai_review_at?:    string | null
  last_sent_review?: { at: string; note: string | null; section_comments?: Record<string, string> } | null
}

interface Step5Payload {
  // Scientific
  design_type?: string; materials?: string; apparatus?: string; procedure?: string
  data_collection_method?: string; data_sheet_url?: string; analysis_plan?: string; error_analysis?: string
  // Engineering
  design_description?: string; build_procedure?: string; test_plan?: string
  // Write-Up (both tracks; target_user is engineering-only)
  purpose_rq?: string; target_user?: string; variables_hypothesis?: string; introduction?: string; acknowledgement?: string; references?: string
  paper_draft?: string
}
interface Step5Row {
  status:      'draft' | 'submitted' | 'approved_draft' | 'completed'
  track:       'scientific_method' | 'engineering_design'
  mentor_note: string | null
  payload:     Step5Payload | null
  updated_at:  string
  mentor_section_comments?: Record<string, string> | null
  submission_note?: string | null
  ai_review?:       MentorReview | null    // cached AI review (auto-run on submit)
  ai_review_at?:    string | null
  last_sent_review?: { at: string; note: string | null; section_comments?: Record<string, string> } | null
}

interface Step6Row {
  status:      'draft' | 'submitted' | 'approved_draft' | 'completed'
  mentor_note: string | null
  payload:     {
    data_folder_url?: string; data_folder_check?: { state: string; reason: string } | null
    photo_folder_url?: string; code_url?: string
    log?: Array<{ id: string; date: string; text: string }>
    materials_actual?: string; procedure_actual?: string; preliminary_observation?: string
  } | null
  updated_at:  string
}

type Action = 'comment' | 'schedule' | 'mark_held' | 'approve' | 'cancel_meeting'

const DIM_LABELS: Record<string, string> = {
  time_fit:    'Time fit',
  budget:      'Budget',
  equipment:   'Equipment',
  skills:      'Skills',
  specificity: 'Specificity',
}
const STATE_GLYPH = (s: string) => s === 'ok' ? '✓' : s === 'caution' ? '⚠' : '?'
const STATE_COLOR = (s: string) =>
  s === 'ok' ? 'success' : s === 'caution' ? 'warning' : 'info'

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
function fmtDateTime(s: string) {
  return new Date(s).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}
function timeSince(s: string | null) {
  if (!s) return 'never'
  const days = Math.floor((Date.now() - new Date(s).getTime()) / 864e5)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return fmtDate(s)
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MentorReviewPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { session, role } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [studentId,     setStudentId]     = useState<string>('')
  const [studentName,   setStudentName]   = useState<string>('')
  const [studentEmail,  setStudentEmail]  = useState<string>('')
  const [studentGrade,  setStudentGrade]  = useState<number | null>(null)
  const [studentSchool, setStudentSchool] = useState<string>('')
  const [studentGender, setStudentGender] = useState<string | null>(null)
  const [className,     setClassName]     = useState<string | null>(null)
  const [lastLogin,     setLastLogin]     = useState<string | null>(null)
  const [selections,    setSelections]    = useState<CategorySelection[]>([])
  const [topics,        setTopics]        = useState<AcceptedTopic[]>([])
  const [finalistIds,   setFinalistIds]   = useState<string[]>([])
  const [topicStatus,   setTopicStatus]   = useState<string>('submitted')
  const [submittedAt,   setSubmittedAt]   = useState<string | null>(null)
  const [meetingScheduledAt, setMeetingScheduledAt] = useState<string | null>(null)
  const [meetingRequestedAt, setMeetingRequestedAt] = useState<string | null>(null)
  const [qa,            setQa]            = useState<QAEntry[]>([])
  const [reviewLog,     setReviewLog]     = useState<ReviewLogEntry[]>([])
  const [versions,      setVersions]      = useState<SubmissionVersion[]>([])
  const [pendingQueue,  setPendingQueue]  = useState<string[]>([])
  const [revisionRequests, setRevisionRequests] = useState<RevisionRequestRow[]>([])
  const [step2Draft,    setStep2Draft]    = useState<Step2DraftRow | null>(null)
  const [step3,         setStep3]         = useState<Step3Row | null>(null)
  const [step4,         setStep4]         = useState<Step4Row | null>(null)
  const [step5,         setStep5]         = useState<Step5Row | null>(null)
  const [step6,         setStep6]         = useState<Step6Row | null>(null)

  // Action panel state
  const [action, setAction] = useState<Action>('comment')
  const [note,   setNote]   = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [impBusy,    setImpBusy]    = useState(false)
  const [cancellingMeeting, setCancellingMeeting] = useState(false)
  const [submitted,  setSubmitted]  = useState(false)
  const [noteDirective, setNoteDirective] = useState('')
  const [noteCdHistory, setNoteCdHistory] = useState<CoDraftTurn[]>([])
  const [noteCdBusy,    setNoteCdBusy]    = useState(false)
  const [noteCdErr,     setNoteCdErr]     = useState<string | null>(null)
  // Set by the active step's review panel so Send-back/Approve also persists the
  // mentor's per-section comments (they previously saved only via a separate
  // "Save comments" button and could fall out of sync with the send-back).
  const sectionFlushRef = useRef<(() => Promise<void>) | null>(null)
  // Only show the full-page spinner (which unmounts every child panel) on the
  // first load or a real project switch. A same-project refetch — token refresh,
  // save-comments onReload, cancel-meeting, revision onDecided — must keep the
  // panels mounted, or unsaved section comments / AI reviews are discarded.
  const loadedProjectRef = useRef<string | null>(null)

  async function load() {
    if (!projectId || !session) return
    if (loadedProjectRef.current !== projectId) setLoading(true)
    setError(null)
    try {
      // Resolve student via project
      const { data: project } = await supabase
        .from('project')
        .select('project_id, student_id, class_id')
        .eq('project_id', projectId)
        .maybeSingle()
      if (!project) { setError('Project not found.'); setLoading(false); return }
      setStudentId(project.student_id)

      // Student profile + user
      const { data: sp } = await supabase
        .from('student_profile')
        .select('student_id, user_id, grade, school_name, gender')
        .eq('student_id', project.student_id)
        .maybeSingle()
      if (sp) {
        setStudentGrade(sp.grade ?? null)
        setStudentSchool(sp.school_name ?? '')
        setStudentGender((sp as { gender?: string | null }).gender ?? null)
        const { data: user } = await supabase
          .from('users')
          .select('first_name, last_name, email, last_login')
          .eq('user_id', sp.user_id)
          .maybeSingle()
        if (user) {
          setStudentName(`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email)
          setStudentEmail(user.email ?? '')
          setLastLogin(user.last_login ?? null)
        }
      }
      // Class
      if (project.class_id) {
        const { data: cls } = await supabase
          .from('class').select('name').eq('class_id', project.class_id).maybeSingle()
        setClassName(cls?.name ?? null)
      }

      // Categories
      const { data: ci } = await supabase
        .from('category_input')
        .select('selections')
        .eq('project_id', projectId)
        .maybeSingle()
      const sels = (ci?.selections ?? []) as any[]
      setSelections(sels.map(s => ({
        category_id: s.category_id, name: s.name, reason: s.reason,
      })))

      // Topics (accepted + rejected)
      const { data: topicRows } = await supabase
        .from('topic_recommendation')
        .select('id, title, description, decision, justification, decided_at, source, finalist_thoughts, evaluation, spanning_categories')
        .eq('project_id', projectId)
        .order('generated_at')
      setTopics((topicRows ?? []) as AcceptedTopic[])

      // Research topic status + finalist ids
      const { data: rt } = await supabase
        .from('research_topic')
        .select('id, finalist_recommendation_ids, status, updated_at')
        .eq('project_id', projectId)
        .maybeSingle()
      setFinalistIds(((rt?.finalist_recommendation_ids ?? []) as string[]))
      setTopicStatus(rt?.status ?? 'draft')
      setSubmittedAt(rt?.updated_at ?? null)

      // Active step1_review meeting for this submission, if any
      let meetingScheduled: string | null = null
      let meetingRequested: string | null = null
      if (rt?.id) {
        const { data: m } = await supabase
          .from('meeting')
          .select('scheduled_at, requested_at, status')
          .eq('research_topic_id', rt.id)
          .eq('kind', 'step1_review')
          .in('status', ['requested', 'scheduled'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        if (m) {
          meetingScheduled = (m as any).scheduled_at ?? null
          meetingRequested = (m as any).requested_at ?? null
        }
      }
      setMeetingScheduledAt(meetingScheduled)
      setMeetingRequestedAt(meetingRequested)

      // Q&A history (only for finalists — that's what's relevant to the review)
      const finalistTopicIds = (rt?.finalist_recommendation_ids ?? []) as string[]
      if (finalistTopicIds.length > 0) {
        const { data: qaRows } = await supabase
          .from('topic_qa_log')
          .select('id, topic_recommendation_id, question, answer, asked_at')
          .in('topic_recommendation_id', finalistTopicIds)
          .order('asked_at')
        setQa((qaRows ?? []) as QAEntry[])
      } else {
        setQa([])
      }

      // Review history (mentor_review_log)
      const { data: logs } = await supabase
        .from('mentor_review_log')
        .select('log_id, action, note, acted_at, reviewer_id')
        .eq('project_id', projectId)
        .order('acted_at', { ascending: true })
      const reviewerIds = Array.from(new Set((logs ?? []).map(l => l.reviewer_id).filter(Boolean) as string[]))
      const reviewerMap: Record<string, string> = {}
      if (reviewerIds.length > 0) {
        const { data: us } = await supabase.from('users')
          .select('user_id, first_name, last_name')
          .in('user_id', reviewerIds)
        for (const u of us ?? []) reviewerMap[u.user_id] = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
      }
      setReviewLog(((logs ?? []).map(l => ({
        ...l,
        reviewer_name: l.reviewer_id ? (reviewerMap[l.reviewer_id] ?? null) : null,
      })) as ReviewLogEntry[]))

      // Submission history — snapshots kept at each submit, for the "what
      // changed since last round" panel.
      const { data: vers } = await supabase
        .from('submission_version')
        .select('step_number, payload, created_at')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true })
      setVersions((vers ?? []) as SubmissionVersion[])

      // Revision requests for this project
      const { data: revRows } = await supabase
        .from('revision_request')
        .select('id, step_number, target_subphase, reason, status, mentor_note, requested_at, decided_at')
        .eq('project_id', projectId)
        .order('requested_at', { ascending: true })
      setRevisionRequests((revRows ?? []) as RevisionRequestRow[])

      // Step 2 draft (if any). Surfaced as its own review panel when status='submitted'.
      const { data: rqRow } = await supabase
        .from('research_question')
        .select('track_committed, status, mentor_note, draft_payload, updated_at, ai_review, ai_review_at')
        .eq('project_id', projectId)
        .maybeSingle()
      setStep2Draft((rqRow as Step2DraftRow | null) ?? null)

      // Step 3 background research (if any).
      const { data: brRow } = await supabase
        .from('background_research')
        .select('status, mentor_note, payload, updated_at, mentor_section_comments, submission_note, ai_review, ai_review_at, last_sent_review')
        .eq('project_id', projectId)
        .maybeSingle()
      setStep3((brRow as Step3Row | null) ?? null)

      // Step 4 hypothesis design (if any).
      const { data: hdRow } = await supabase
        .from('hypothesis_design')
        .select('status, track, study_mode, mentor_note, payload, updated_at, mentor_section_comments, ai_review, ai_review_at, last_sent_review')
        .eq('project_id', projectId)
        .maybeSingle()
      setStep4((hdRow as Step4Row | null) ?? null)

      // Step 5 experimental design (if any).
      const { data: edRow } = await supabase
        .from('experiment_design')
        .select('status, track, mentor_note, payload, updated_at, mentor_section_comments, submission_note, ai_review, ai_review_at, last_sent_review')
        .eq('project_id', projectId)
        .maybeSingle()
      setStep5((edRow as Step5Row | null) ?? null)

      // Step 6 experiment record (if any).
      const { data: erRow } = await supabase
        .from('experiment_record')
        .select('status, mentor_note, payload, updated_at')
        .eq('project_id', projectId)
        .maybeSingle()
      setStep6((erRow as Step6Row | null) ?? null)

      // Other pending submissions for "next" navigation
      const { data: otherSubmissions } = await supabase
        .from('research_topic')
        .select('project_id')
        .eq('status', 'submitted')
        .neq('project_id', projectId)
        .order('updated_at', { ascending: true })
      setPendingQueue((otherSubmissions ?? []).map(o => o.project_id))

      // Default action heuristic — Step 1 only. A later step must never open on
      // approve: once Step 1 is approved there is always exactly one finalist,
      // so a mentor writing a revision note would approve without noticing.
      //   - meeting already scheduled  → mark_held (most likely next step)
      //   - single finalist            → approve
      //   - otherwise                  → comment
      if (rt?.status !== 'submitted') setAction('comment')
      else if (meetingScheduled) setAction('mark_held')
      else if (finalistTopicIds.length === 1) setAction('approve')
      else setAction('comment')
    } catch (e: any) {
      setError(e.message)
    } finally {
      loadedProjectRef.current = projectId
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId, session])

  // Derived
  const acceptedTopics = topics.filter(t => t.decision === 'accept')
  const rejectedTopics = topics.filter(t => t.decision === 'reject')
  const finalists      = finalistIds.map(id => topics.find(t => t.id === id)).filter(Boolean) as AcceptedTopic[]
  // Which step is under review drives the decision panel. Precedence: a
  // submitted Step 3 → step3; else a submitted Step 2 draft → step2; else Step 1.
  const reviewCtx: 'step6' | 'step5' | 'step4' | 'step3' | 'step2' | 'step1' =
    step6?.status === 'submitted' ? 'step6'
    : step5?.status === 'submitted' ? 'step5'
    : step4?.status === 'submitted' ? 'step4'
    : step3?.status === 'submitted' ? 'step3'
    : step2Draft?.status === 'submitted' ? 'step2'
    : 'step1'
  const isApprovable   = reviewCtx === 'step1' ? finalistIds.length === 1 : true
  // Note-required actions: comment + mark_held. Schedule + approve note is optional.
  const noteRequired   = action === 'comment' || action === 'mark_held'
  const noteOk         = !noteRequired || note.trim().length >= 50
  const canAct         = reviewCtx === 'step6' ? step6?.status === 'submitted'
    : reviewCtx === 'step5' ? step5?.status === 'submitted'
    : reviewCtx === 'step4' ? step4?.status === 'submitted'
    : reviewCtx === 'step3' ? step3?.status === 'submitted'
    : reviewCtx === 'step2' ? step2Draft?.status === 'submitted'
    : topicStatus === 'submitted'
  // Status of the step actually under review — NOT always Step 1's topic status
  // (a student past Step 1 has topicStatus 'completed', which must not make a
  // later-step review look approved/read-only).
  const ctxStatus =
    reviewCtx === 'step6' ? (step6?.status ?? 'draft')
    : reviewCtx === 'step5' ? (step5?.status ?? 'draft')
    : reviewCtx === 'step4' ? (step4?.status ?? 'draft')
    : reviewCtx === 'step3' ? (step3?.status ?? 'draft')
    : reviewCtx === 'step2' ? (step2Draft?.status ?? 'draft')
    : topicStatus

  // Clear any note carried over from a previously-reviewed project.
  useEffect(() => { setNote(''); setNoteDirective(''); setNoteCdHistory([]); setNoteCdErr(null) }, [projectId])
  // Step 3 send-back: default the note to a short instruction pointing the
  // student at the per-section comments (they hold the detailed feedback).
  useEffect(() => {
    if (reviewCtx === 'step3') setNote(prev => (prev.trim() ? prev : STEP3_SENDBACK_DEFAULT))
    if (reviewCtx === 'step4') setNote(prev => (prev.trim() ? prev : STEP4_SENDBACK_DEFAULT))
    if (reviewCtx === 'step5') setNote(prev => (prev.trim() ? prev : STEP5_SENDBACK_DEFAULT))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewCtx])
  const meetingAlreadyScheduled = !!meetingScheduledAt
  const meetingAlreadyRequested = !!meetingRequestedAt && !meetingAlreadyScheduled
  // An ongoing message thread exists once anyone has posted a chat message.
  const hasConversation = reviewLog.some(l => l.action === 'message' || l.action === 'student_message')

  // Directive co-draft: the mentor tells the AI what to say and it writes the
  // note (faster than writing then polishing). Same loop as the section
  // comments — it starts from whatever is in the note and revises per direction.
  async function noteCoDraft(override?: string) {
    const d = (override ?? noteDirective).trim()
    if (!d || noteCdBusy || submitting) return
    setNoteCdBusy(true); setNoteCdErr(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: {
          project_id:      projectId,
          section_label:   `${reviewCtx} decision note to the student`,
          current_comment: override ? '' : note,
          directive:       d,
          history:         override ? [] : noteCdHistory,
        },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      if (!override) setNoteCdHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setNote(data.comment as string)
      if (!override) setNoteDirective('')
    } catch (e) {
      setNoteCdErr(e instanceof Error ? e.message : 'Could not draft — please try again.')
    } finally {
      setNoteCdBusy(false)
    }
  }

  // One-click: draft a full, personalized message for the student. Prefers the
  // AI review's ready-made overall message (which names the required fixes);
  // otherwise generates one from a preset direction. Alpha — refine via cases.
  async function draftFullMessage() {
    if (noteCdBusy || submitting) return
    const review = reviewCtx === 'step3' ? step3?.ai_review
      : reviewCtx === 'step4' ? step4?.ai_review
      : reviewCtx === 'step5' ? step5?.ai_review : null
    const cached = review?.overall_comment ?? ''
    if (cached.trim()) { setNote(cached); setNoteCdErr(null); return }

    /* At a full gate there is no cached comment — review-verdict.ts drops it.
       Without this branch the fallback below would write a send-back message
       for a submission with nothing wrong with it, which is the whole thing
       the rule exists to stop. An approval note instead: what is strong, and
       the judge questions to rehearse. */
    if (review?.overall?.gate_met) {
      const prep = (review.prep_questions ?? []).map(q => `— ${q.question}${q.note ? ` (${q.note})` : ''}`).join('\n')
      await noteCoDraft(
        'Write the approval message I will send this student. Their work meets every requirement for this step, so do NOT ask for fixes, changes or a resubmission — approve it plainly and say the step is complete. '
        + (prep
            ? `Then tell them what to rehearse before the judge interview, in their own project's terms, covering these and nothing else:\n${prep}\n`
            : '')
        + 'Keep it short, warm, specific to their work, and plain — no headings.')
      return
    }

    await noteCoDraft('Write the full message I will send this student with my review. Lead with the one or two most important things they need to fix, ask them to make those and resubmit, and say the rest of the comments are optional. Keep it short, warm, specific to their work, and plain — no headings.')
  }

  async function performAction() {
    if (!projectId) return

    // Step 2 / 3 / 4 / 5 draft review — approve or send back with comment (same shape).
    if (reviewCtx === 'step2' || reviewCtx === 'step3' || reviewCtx === 'step4' || reviewCtx === 'step5' || reviewCtx === 'step6') {
      const a = action === 'approve' ? 'approve' : 'comment'
      if (a === 'comment' && !noteOk) {
        setError('A substantive note is required (at least 50 characters).')
        return
      }
      const fn = reviewCtx === 'step6' ? 'step6-mentor-review'
        : reviewCtx === 'step5' ? 'step5-mentor-review'
        : reviewCtx === 'step4' ? 'step4-mentor-review'
        : reviewCtx === 'step3' ? 'step3-mentor-review' : 'step2-mentor-review'
      setSubmitting(true); setError(null)
      // Persist the mentor's per-section comments as part of this action so they
      // reach the student together with the note (not just via "Save comments").
      if (sectionFlushRef.current) {
        try { await sectionFlushRef.current() }
        catch { setError('Could not save your section comments — please try again.'); setSubmitting(false); return }
      }
      const { data, error: invokeErr } = await supabase.functions.invoke(fn, {
        body: { project_id: projectId, action: a, mentor_note: note.trim() || null },
      })
      if (invokeErr) { setError(await readFunctionError(invokeErr)); setSubmitting(false); return }
      if (data?.error) { setError(data.error); setSubmitting(false); return }
      setSubmitting(false); setSubmitted(true)
      return
    }

    if (action === 'approve' && !isApprovable) {
      setError('Approve is only available when the student submitted exactly one finalist.')
      return
    }
    if (action === 'mark_held' && !meetingAlreadyScheduled) {
      setError('No meeting is scheduled. Schedule a meeting first.')
      return
    }
    if (noteRequired && !noteOk) {
      setError('A substantive note is required (at least 50 characters).')
      return
    }
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('step1-mentor-review', {
      body: {
        project_id:   projectId,
        action,
        mentor_note:  note.trim() || null,
      },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSubmitting(false); return }
    if (data?.error) { setError(data.error); setSubmitting(false); return }
    setSubmitting(false)
    // Cancelling a meeting isn't a terminal decision — the submission stays open
    // for review. Refresh so the meeting banner clears and the mentor can then
    // approve or send back, rather than locking the panel.
    if (action === 'cancel_meeting') {
      setAction('comment'); setNote('')
      await load()
      return
    }
    setSubmitted(true)
  }

  // Cancel an active meeting from anywhere — independent of the step under
  // review or whether the submission is currently 'submitted'. A meeting can
  // outlive the Step 1 review (e.g. the topic gets approved), and it still
  // needs to be cancellable once it's no longer needed.
  async function cancelMeetingRequest() {
    if (!projectId || cancellingMeeting) return
    setCancellingMeeting(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step1-mentor-review', {
        body: { project_id: projectId, action: 'cancel_meeting', mentor_note: null },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel the meeting — please try again.')
    } finally {
      setCancellingMeeting(false)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────

  if (role !== 'admin' && role !== 'office_manager' && role !== 'instructor' && role !== 'mentor') {
    return <Box sx={{ p: 4 }}><Alert severity="warning">Staff only.</Alert></Box>
  }

  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1280 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/students')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Reviewing {studentName || 'student'}'s submission
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ctxStatus === 'submitted'
              ? <>Submitted{reviewCtx === 'step1' && submittedAt ? ` ${timeSince(submittedAt)} · ${finalists.length} finalist${finalists.length !== 1 ? 's' : ''}` : ''} · awaiting your review</>
              : (ctxStatus === 'completed' || ctxStatus === 'approved_draft')
                ? <>Status: <strong>Approved</strong>. Read-only.</>
                : <>Status: <strong>Draft</strong> (back with student) — read-only until they resubmit</>}
          </Typography>
        </Box>
        {studentId && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => window.open(`/view-as/${studentId}/dashboard`, '_blank')}
            >
              View as student
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={impBusy}
              startIcon={impBusy ? <CircularProgress size={14} color="inherit" /> : <LoginIcon />}
              onClick={async () => {
                if (!studentId) return
                setImpBusy(true); setError(null)
                try {
                  await startImpersonation(studentId, studentName || 'this student')
                  navigate('/')
                } catch (e: any) {
                  setError(e.message ?? 'Could not log in as the student.')
                  setImpBusy(false)
                }
              }}
            >
              {impBusy ? 'Logging in…' : 'Log in as student'}
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* An active meeting can outlive the Step 1 decision panel (topic approved,
          or a later step under review). Surface a cancel option regardless, so a
          no-longer-needed meeting is never stuck. */}
      {(meetingAlreadyRequested || meetingAlreadyScheduled) && (reviewCtx !== 'step1' || !canAct) && (
        <Alert
          severity="info" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={cancelMeetingRequest} disabled={cancellingMeeting}>
              {cancellingMeeting ? 'Cancelling…' : 'Cancel meeting'}
            </Button>
          }
        >
          <strong>
            {meetingAlreadyScheduled ? `Meeting scheduled for ${fmtDateTime(meetingScheduledAt!)}` : 'Meeting requested'}.
          </strong>{' '}
          If your conversation resolved it, cancel the meeting here.
        </Alert>
      )}
      {submitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {action === 'approve'
            ? (reviewCtx === 'step6'
                ? 'Approved. Step 7 unlocks for the student.'
                : reviewCtx === 'step5'
                ? 'Approved. Step 6 unlocks for the student.'
                : reviewCtx === 'step4'
                  ? 'Approved. Step 5 unlocks for the student.'
                  : reviewCtx === 'step3'
                    ? 'Approved. Step 4 unlocks for the student.'
                    : reviewCtx === 'step2'
                      ? 'Approved. Step 3 unlocks for the student.'
                      : 'Approved. Step 2 unlocks for the student.')
            : 'Sent back to the student.'}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 380px' }, gap: 3, mt: 2 }}>
        {/* ── LEFT — student context ────────────────────────────────── */}
        <Box>
          {/* Student summary */}
          <Section title="Student">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{studentName}</Typography>
              <Typography variant="caption" color="text.secondary">{studentEmail}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {studentGrade != null && `Grade ${studentGrade}`}
              {studentGender && ` · ${studentGender === 'male' ? 'Male' : 'Female'}`}
              {studentSchool && ` · ${studentSchool}`}
              {className && ` · Class: ${className}`}
              {lastLogin && ` · Last seen ${timeSince(lastLogin)}`}
            </Typography>
          </Section>

          {/* Track switch — only once background research (Step 3) exists;
              matches switch-project-track's own validation. */}
          {projectId && step3 && step2Draft?.track_committed && (
            <TrackSwitchPanel
              projectId={projectId}
              currentTrack={step2Draft.track_committed}
              studentName={studentName}
              onSwitched={load}
            />
          )}
          {projectId && <TrackSwitchArchivePanel projectId={projectId} />}

          {/* What the student changed since their previous submission */}
          <SubmissionChanges versions={versions.filter(v => v.step_number === Number(reviewCtx.replace('step', '')))} />

          {/* Step 6 experiment awaiting review */}
          {step6 && step6.status === 'submitted' && (
            <Section title="Step 6 — Experiment for review">
              <Step6ReviewPanel row={step6} />
            </Section>
          )}

          {/* Step 5 experimental design awaiting review */}
          {step5 && step5.status === 'submitted' && (
            <Section title="Step 5 — Experimental design for review">
              <Step5ReviewPanel key={projectId} row={step5} projectId={projectId!} onReload={load} onRegisterFlush={fn => { sectionFlushRef.current = fn }} onUseComment={(t) => { setNote(t); setAction('comment') }} />
            </Section>
          )}

          {/* Step 4 hypothesis awaiting review */}
          {step4 && step4.status === 'submitted' && (
            <Section title="Step 4 — Hypothesis for review">
              <Step4ReviewPanel key={projectId} row={step4} projectId={projectId!} onReload={load} onRegisterFlush={fn => { sectionFlushRef.current = fn }} onUseComment={(t) => { setNote(t); setAction('comment') }} />
            </Section>
          )}

          {/* Step 3 background research awaiting review */}
          {step3 && step3.status === 'submitted' && (
            <Section title="Step 3 — Background research for review">
              <Step3ReviewPanel key={projectId} row={step3} projectId={projectId!} onReload={load} onRegisterFlush={fn => { sectionFlushRef.current = fn }} onUseComment={(t) => { setNote(t); setAction('comment') }} />
            </Section>
          )}

          {/* Step 2 draft awaiting review */}
          {step2Draft && step2Draft.status === 'submitted' && (
            <Section title="Step 2 — Draft for review">
              <Step2DraftPanel draft={step2Draft} />
            </Section>
          )}

          {/* AI review (mentor-only) — assesses the draft research question + drafts a comment */}
          {reviewCtx === 'step2' && step2Draft && step2Draft.status === 'submitted' && (
            <Section title="AI review (mentor-only)">
              <Step2AiReview row={step2Draft} projectId={projectId!} onUseComment={(t) => { setNote(t); setAction('comment') }} />
            </Section>
          )}

          {/* Earlier approved steps — collapsed, read-only, so the mentor can check
              prior work for context while reviewing the current step. */}
          {reviewCtx === 'step6' && step5 && step5.status !== 'submitted' && step5.payload && (
            <PriorContext title="Step 5 — Experimental design (approved)">
              <Step5ReviewPanel row={step5} projectId={projectId!} readOnly />
            </PriorContext>
          )}
          {reviewCtx === 'step5' && step4 && step4.status !== 'submitted' && step4.payload && (
            <PriorContext title="Step 4 — Hypothesis (approved)">
              <Step4ReviewPanel row={step4} projectId={projectId!} readOnly />
            </PriorContext>
          )}
          {(reviewCtx === 'step4' || reviewCtx === 'step5') && step3 && step3.status !== 'submitted' && step3.payload && (
            <PriorContext title="Step 3 — Background research (approved)">
              <Step3ReviewPanel row={step3} projectId={projectId!} readOnly />
            </PriorContext>
          )}
          {(reviewCtx === 'step3' || reviewCtx === 'step4' || reviewCtx === 'step5') && step2Draft && step2Draft.status !== 'submitted' && (
            <PriorContext title="Step 2 — Research question (approved)">
              <Step2DraftPanel draft={step2Draft} />
            </PriorContext>
          )}

          {/* Conversation with student — shown while a submission is in flight,
              or whenever a message thread already exists (so a pending student
              message can still be answered after the topic is sent back to draft). */}
          {(topicStatus === 'submitted' || step2Draft?.status === 'submitted' || step3?.status === 'submitted' || step4?.status === 'submitted' || step5?.status === 'submitted' || step6?.status === 'submitted' || hasConversation) && (
            <Section title="Conversation with student">
              <ConversationThread projectId={projectId!} editable={true} viewerSide="staff" />
            </Section>
          )}

          {/* Pending revision requests — high priority; mentor decides before topic review */}
          {revisionRequests.filter(r => r.status === 'pending').length > 0 && (
            <Section title="Pending revision request">
              {revisionRequests.filter(r => r.status === 'pending').map(r => (
                <RevisionRequestRow
                  key={r.id}
                  request={r}
                  onDecided={() => load()}
                />
              ))}
            </Section>
          )}

          {/* Categories */}
          <Section title="Categories chosen">
            {selections.length === 0 ? (
              <Typography variant="body2" color="text.secondary">(none recorded)</Typography>
            ) : (
              selections.map(s => (
                <Box key={s.category_id} sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
                    "{s.reason}"
                  </Typography>
                </Box>
              ))
            )}
          </Section>

          {/* Finalists — the meat */}
          <Section title={`Finalists submitted${finalists.length > 0 ? ` (${finalists.length})` : ''}`}>
            {finalists.length === 0 ? (
              <Typography variant="body2" color="text.secondary">(no finalists submitted)</Typography>
            ) : (
              finalists.map((t, i) => (
                <FinalistCard key={t.id} idx={i} topic={t} qa={qa.filter(q => q.topic_recommendation_id === t.id)} />
              ))
            )}
          </Section>

          {/* AI review (mentor-only) — assesses the finalists + routes to AI Lab */}
          {reviewCtx === 'step1' && finalists.length > 0 && (
            <Section title="AI review (mentor-only)">
              <Step1AiReview projectId={projectId!} onUseComment={(t) => { setNote(t); setAction('comment') }} />
            </Section>
          )}

          {/* Other accepted (not in finalists) */}
          {acceptedTopics.filter(t => !finalistIds.includes(t.id)).length > 0 && (
            <Section title="Other accepted topics (not in finalists)">
              {acceptedTopics.filter(t => !finalistIds.includes(t.id)).map(t => (
                <Box key={t.id} sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{t.description}</Typography>
                  {t.justification && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                      Why accepted: "{t.justification}"
                    </Typography>
                  )}
                </Box>
              ))}
            </Section>
          )}

          {/* Rejected — collapsed-style summary */}
          {rejectedTopics.length > 0 && (
            <Section title={`Considered and declined (${rejectedTopics.length})`}>
              {rejectedTopics.map(t => (
                <Box key={t.id} sx={{ mb: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>{t.title}</Typography>
                  {t.justification && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{t.justification}"
                    </Typography>
                  )}
                </Box>
              ))}
            </Section>
          )}

          {/* Round history */}
          {reviewLog.length > 0 && (
            <Section title="Review history">
              {reviewLog.map(l => (
                <Box key={l.log_id} sx={{
                  mb: 1.5, p: 1.5, borderRadius: 1.25,
                  bgcolor: 'rgba(0,0,0,0.02)',
                  border: '1px solid', borderColor: 'divider',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={
                        l.action === 'approve'        ? 'Approved'
                        : l.action === 'schedule'       ? 'Meeting requested'
                        : l.action === 'mark_held'      ? 'Meeting held'
                        : l.action === 'cancel_meeting' ? 'Meeting cancelled'
                        :                                 'Comment'
                      }
                      color={
                        l.action === 'approve'        ? 'success'
                        : l.action === 'schedule'       ? 'info'
                        : l.action === 'mark_held'      ? 'primary'
                        : l.action === 'cancel_meeting' ? 'default'
                        :                                 'warning'
                      }
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {fmtDateTime(l.acted_at)}{l.reviewer_name && ` · ${l.reviewer_name}`}
                    </Typography>
                  </Box>
                  {l.note && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {l.note}
                    </Typography>
                  )}
                </Box>
              ))}
            </Section>
          )}

          {/* Embedded student journal + documentation (read-only) */}
          <Section title="Student's full record">
            {projectId && studentId && (
              <JournalTab
                projectId={projectId}
                studentId={studentId}
                isTrial={false}
                viewerIsStudent={false}
              />
            )}
          </Section>
        </Box>

        {/* ── RIGHT — sticky decision panel ─────────────────────────── */}
        <Box>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                Your decision
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                {reviewCtx === 'step6'
                  ? 'Reviewing the Step 6 preliminary observation. Pick one, then confirm.'
                  : reviewCtx === 'step5'
                  ? 'Reviewing the Step 5 experimental design. Pick one, then confirm.'
                  : reviewCtx === 'step4'
                    ? 'Reviewing the Step 4 hypothesis. Pick one, then confirm.'
                    : reviewCtx === 'step3'
                      ? 'Reviewing the Step 3 background research. Pick one, then confirm.'
                      : reviewCtx === 'step2'
                        ? 'Reviewing the Step 2 draft. Pick one, then confirm.'
                        : 'Reviewing the Step 1 topic. Pick one, fill in details, then confirm.'}
              </Typography>

              {!canAct && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {(ctxStatus === 'completed' || ctxStatus === 'approved_draft')
                    ? 'This submission has been approved — no further action needed.'
                    : 'Currently in draft with the student. Wait for them to resubmit.'}
                </Alert>
              )}

              {/* Meeting banners — topic (Step 1) review only. */}
              {reviewCtx === 'step1' && meetingAlreadyRequested && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Meeting requested.</strong> Office is contacting the parent to find a time.
                  You'll see the scheduled time here and on your dashboard once it's confirmed.
                </Alert>
              )}
              {reviewCtx === 'step1' && meetingAlreadyScheduled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Meeting scheduled:</strong> {fmtDateTime(meetingScheduledAt!)}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    After the meeting, choose <em>Mark meeting held</em> with a summary of what you
                    discussed. Sending back with a comment will cancel the meeting.
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <ActionRadio
                  label="Send back with comment"
                  desc="Returns the submission to draft with your note. Student revises and resubmits."
                  selected={action === 'comment'}
                  onClick={() => {
                    setAction('comment')
                    if (reviewCtx === 'step3') setNote(prev => (prev.trim() ? prev : STEP3_SENDBACK_DEFAULT))
                    if (reviewCtx === 'step4') setNote(prev => (prev.trim() ? prev : STEP4_SENDBACK_DEFAULT))
                    if (reviewCtx === 'step5') setNote(prev => (prev.trim() ? prev : STEP5_SENDBACK_DEFAULT))
                  }}
                  disabled={!canAct}
                />

                {reviewCtx === 'step1' && (
                  <>
                    <ActionRadio
                      label="Request a meeting"
                      desc="Office contacts the parent to find a time. You'll see the scheduled meeting on your dashboard once confirmed."
                      selected={action === 'schedule'}
                      onClick={() => setAction('schedule')}
                      disabled={!canAct}
                    />
                    {/* Sequel action — only shown after a meeting is on the books. */}
                    {meetingAlreadyScheduled && (
                      <ActionRadio
                        label="Mark meeting held"
                        desc="Record what you discussed; submission returns to draft for the student to refine."
                        selected={action === 'mark_held'}
                        onClick={() => setAction('mark_held')}
                        disabled={!canAct}
                      />
                    )}
                    {/* Drop a no-longer-needed meeting (e.g. resolved at office hours). */}
                    {(meetingAlreadyRequested || meetingAlreadyScheduled) && (
                      <ActionRadio
                        label="Cancel meeting request"
                        desc="The meeting is no longer needed (e.g. resolved at office hours). Removes the request; the submission stays for you to approve or send back."
                        selected={action === 'cancel_meeting'}
                        onClick={() => setAction('cancel_meeting')}
                        disabled={!canAct}
                      />
                    )}
                    <ActionRadio
                      label="Approve final topic"
                      desc={isApprovable
                        ? "Approves the single finalist as the student's topic. Step 2 unlocks."
                        : 'Disabled — student must commit to exactly one finalist before approval.'}
                      selected={action === 'approve'}
                      onClick={() => isApprovable && setAction('approve')}
                      disabled={!canAct || !isApprovable}
                      primary
                    />
                  </>
                )}

                {reviewCtx === 'step2' && (
                  <ActionRadio
                    label="Approve draft and advance to Step 3"
                    desc="Approves the Step 2 draft. Step 3 background research unlocks for the student."
                    selected={action === 'approve'}
                    onClick={() => setAction('approve')}
                    disabled={!canAct}
                    primary
                  />
                )}

                {reviewCtx === 'step3' && (
                  <ActionRadio
                    label="Approve background research and advance to Step 4"
                    desc="Approves the Step 3 background research. Step 4 unlocks for the student."
                    selected={action === 'approve'}
                    onClick={() => {
                      setAction('approve')
                      // Don't carry the send-back instruction into an approval.
                      setNote(prev => (prev === STEP3_SENDBACK_DEFAULT ? '' : prev))
                    }}
                    disabled={!canAct}
                    primary
                  />
                )}

                {reviewCtx === 'step4' && (
                  <ActionRadio
                    label="Approve & advance to Step 5"
                    desc="Approves the Step 4 hypothesis. Step 5 unlocks for the student."
                    selected={action === 'approve'}
                    onClick={() => {
                      setAction('approve')
                      // Don't carry the send-back instruction into an approval.
                      setNote(prev => (prev === STEP4_SENDBACK_DEFAULT ? '' : prev))
                    }}
                    disabled={!canAct}
                    primary
                  />
                )}

                {reviewCtx === 'step6' && (
                  <ActionRadio
                    label="Approve & advance to Step 7"
                    desc="Approves the Step 6 preliminary observation. Step 7 unlocks for the student."
                    selected={action === 'approve'}
                    onClick={() => setAction('approve')}
                    disabled={!canAct}
                    primary
                  />
                )}

                {reviewCtx === 'step5' && (
                  <ActionRadio
                    label="Approve & advance to Step 6"
                    desc="Approves the Step 5 experimental design. Step 6 unlocks for the student."
                    selected={action === 'approve'}
                    onClick={() => {
                      setAction('approve')
                      // Don't carry the send-back instruction into an approval.
                      setNote(prev => (prev === STEP5_SENDBACK_DEFAULT ? '' : prev))
                    }}
                    disabled={!canAct}
                    primary
                  />
                )}
              </Box>

              {/* Note input — different per action */}
              {(action === 'comment' || action === 'mark_held') && (
                <TextField
                  fullWidth multiline minRows={8} maxRows={28} size="small"
                  required
                  label={action === 'mark_held' ? 'Meeting notes' : 'Comment for the student'}
                  placeholder={action === 'mark_held'
                    ? 'Summarize what you discussed. What did the student commit to thinking about? What did they push back on?'
                    : reviewCtx === 'step6'
                      ? 'Be specific — reference their preliminary observation, their log, or their data folder. What should they look at or add before resubmitting?'
                    : reviewCtx === 'step5'
                      ? 'Be specific — reference their design type, materials/apparatus, procedure, and data plan. The per-section comments hold the detail; this note points them there.'
                      : reviewCtx === 'step4'
                      ? 'Be specific — reference their variables, their hypothesis + grounding, and the measurement/tool. The per-section comments hold the detail; this note points them there.'
                      : reviewCtx === 'step3'
                        ? 'Be specific — reference their key papers, write-up (what the research shows / the gap), refined question, and the feasibility answers. What should they strengthen before resubmitting?'
                        : reviewCtx === 'step2'
                          ? 'Be specific — reference their variables, draft question/statement, method, and any AI flags. What should they refine before resubmitting?'
                          : 'Be specific — reference their categories, their finalists, the AI flags. What do you want them to consider before resubmitting?'}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  disabled={!canAct || submitting}
                  helperText={
                    note.trim().length < 50
                      ? `${note.trim().length} / 50 minimum — write something the student can actually use.`
                      : 'Looks good.'
                  }
                  error={note.trim().length > 0 && note.trim().length < 50}
                  sx={{ mb: 2 }}
                />
              )}

              {action === 'schedule' && (
                <TextField
                  fullWidth multiline rows={3} size="small"
                  label="Note for the student (shown to them while they wait)"
                  placeholder="What you'd like to discuss, what to think about before the meeting, plus any scheduling context for the office."
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  disabled={!canAct || submitting}
                  sx={{ mb: 2 }}
                />
              )}

              {action === 'approve' && (
                <TextField
                  fullWidth multiline minRows={8} maxRows={28} size="small"
                  label="Optional note for the student"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  disabled={!canAct || submitting}
                  sx={{ mb: 2 }}
                />
              )}

              {action === 'cancel_meeting' && (
                <TextField
                  fullWidth multiline rows={2} size="small"
                  label="Optional reason (for the record)"
                  placeholder="e.g. Student came to office hours and we resolved it there."
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  disabled={!canAct || submitting}
                  sx={{ mb: 2 }}
                />
              )}

              {/* Directive co-draft — tell the AI what to say and it writes the
                  note (faster than write-then-polish). Revises whatever is in
                  the box above; iterate with more directions. */}
              {action !== 'cancel_meeting' && (
                <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <AutoFixHighIcon fontSize="small" color="primary" />
                    <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Tell the AI what to write</Typography>
                    {/* Steps 3, 4, and 5 have their own dedicated "Final message to
                        the student" box (draft + Use as my comment), so the
                        one-click preset is redundant there. */}
                    {reviewCtx !== 'step3' && reviewCtx !== 'step4' && reviewCtx !== 'step5' && (
                    <Button
                      size="small" variant="outlined"
                      onClick={draftFullMessage}
                      disabled={!canAct || submitting || noteCdBusy}
                    >
                      {noteCdBusy ? 'Drafting…' : '✨ Draft a full message'}
                    </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {reviewCtx === 'step3' || reviewCtx === 'step4' || reviewCtx === 'step5'
                      ? 'Give a direction below and the AI writes the note — or use the “Final message to the student” box above.'
                      : 'One click drafts a complete message from your review (the key fixes + resubmit) — or give a direction below.'}
                  </Typography>
                  {noteCdHistory.filter(t => t.role === 'mentor').map((t, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>↳ {t.text}</Typography>
                  ))}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth size="small" multiline minRows={1} maxRows={5}
                      placeholder="e.g. tell him to add published air-quality health data to his intro, and to describe the method of his model paper — warm, encouraging"
                      value={noteDirective} onChange={e => setNoteDirective(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) noteCoDraft() }}
                      disabled={!canAct || submitting || noteCdBusy}
                    />
                    <Button
                      variant="contained" size="small" sx={{ mt: 0.25 }}
                      onClick={() => noteCoDraft()}
                      disabled={!canAct || submitting || noteCdBusy || !noteDirective.trim()}
                    >
                      {noteCdBusy ? <CircularProgress size={16} color="inherit" /> : 'Write'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    It writes the note above for you to review and edit. Give another direction to refine it.
                  </Typography>
                  {noteCdErr && <Alert severity="error" sx={{ mt: 1 }}>{noteCdErr}</Alert>}
                </Box>
              )}

              <Button
                variant="contained" fullWidth
                color={action === 'approve' ? 'success' : action === 'cancel_meeting' ? 'warning' : 'primary'}
                onClick={performAction}
                disabled={!canAct || submitting || submitted || noteCdBusy || (noteRequired && !noteOk)}
              >
                {submitting
                  ? <CircularProgress size={18} color="inherit" />
                  : action === 'approve'        ? (reviewCtx === 'step6' ? 'Approve & advance to Step 7' : reviewCtx === 'step5' ? 'Approve & advance to Step 6' : reviewCtx === 'step4' ? 'Approve & advance to Step 5' : reviewCtx === 'step3' ? 'Approve & advance to Step 4' : reviewCtx === 'step2' ? 'Approve draft and advance to Step 3' : 'Approve final topic')
                  : action === 'schedule'       ? 'Request a meeting'
                  : action === 'mark_held'      ? 'Mark meeting held'
                  : action === 'cancel_meeting' ? 'Cancel meeting request'
                  :                                'Send back with comment'}
              </Button>

              {/* Next student navigation after submit */}
              {submitted && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                  {pendingQueue.length > 0 ? (
                    <Button
                      variant="outlined" fullWidth
                      onClick={() => navigate(`/review/${pendingQueue[0]}`)}
                    >
                      Next pending review →
                    </Button>
                  ) : (
                    <Button variant="outlined" fullWidth onClick={() => navigate('/students')}>
                      Back to Students
                    </Button>
                  )}
                </Box>
              )}
              {!submitted && pendingQueue.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                  {pendingQueue.length} other submission{pendingQueue.length !== 1 ? 's' : ''} awaiting review.
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Friendly labels for payload fields across steps; unknown keys are humanized.
const CHANGE_LABELS: Record<string, string> = {
  question: 'Research question', iv: 'Independent variable', dv: 'Dependent variable',
  cv: 'Controlled variables', trials: 'Trials', blocks: 'Hypotheses & variables',
  track: 'Track', narrowed_topic: 'Narrowed topic', statement_of_purpose: 'Statement of purpose',
  purpose: 'Purpose', the_gap: 'The gap', what_research_shows: 'What the research shows',
  principles: 'Principles', key_concepts: 'Key concepts', significance: 'Significance',
  conclusion: 'Conclusion', review_paper: 'Review paper', model_paper: 'Model paper',
  finalists: 'Finalists', finalist_recommendation_ids: 'Finalist selection',
}
function changeLabel(key: string): string {
  return CHANGE_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
// Internal / machine-written keys that aren't student-authored content — never
// show them in the diff (they change on every save and render as noise JSON).
const DIFF_HIDE = new Set(['ai_feedback', 'notebook_access', 'study_mode', 'updated_at'])

// Render any field value as readable text — never raw JSON. Structured fields
// (papers, hypothesis blocks, source lists) become labeled lines.
function valueText(v: unknown, depth = 0): string {
  if (v == null || v === '') return '(empty)'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) {
    const items = v.filter(x => x != null && x !== '')
    if (items.length === 0) return '(empty)'
    return items.map((item, i) => `${i + 1}. ${valueText(item, depth + 1).replace(/\n/g, '\n   ')}`).join('\n')
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>).filter(([, val]) => val != null && val !== '')
    if (entries.length === 0) return '(empty)'
    const pad = '  '.repeat(depth)
    return entries.map(([key, val]) => `${pad}${changeLabel(key)}: ${valueText(val, depth + 1)}`).join('\n')
  }
  return String(v)
}

// Shows what the student changed between their previous submission and the
// current one for this step. Renders nothing until there are ≥2 snapshots
// (i.e. at least one resubmission). Snapshots come from submission_version.
function SubmissionChanges({ versions }: { versions: SubmissionVersion[] }) {
  if (versions.length < 2) return null
  const prev = versions[versions.length - 2].payload ?? {}
  const cur  = versions[versions.length - 1].payload ?? {}
  const prevAt = versions[versions.length - 2].created_at

  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(cur)]))
    .filter(k => !DIFF_HIDE.has(k))
    .filter(k => JSON.stringify((prev as Record<string, unknown>)[k]) !== JSON.stringify((cur as Record<string, unknown>)[k]))

  if (keys.length === 0) {
    return (
      <Section title="Changes since last submission">
        <Typography variant="body2" color="text.secondary">
          Resubmitted with no changes to the recorded fields.
        </Typography>
      </Section>
    )
  }

  const box = (text: string, tone: 'before' | 'after') => (
    <Box sx={{
      p: 1, borderRadius: 1, whiteSpace: 'pre-wrap', overflowX: 'auto',
      fontFamily: text.startsWith('{') || text.startsWith('[') ? 'monospace' : 'inherit',
      fontSize: '0.8rem', lineHeight: 1.5,
      bgcolor: tone === 'before' ? 'rgba(0,0,0,0.04)' : 'rgba(46,125,50,0.08)',
      color: tone === 'before' ? 'text.secondary' : 'text.primary',
    }}>{text}</Box>
  )

  return (
    <Section title="Changes since last submission">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {keys.length} field{keys.length !== 1 ? 's' : ''} changed since {new Date(prevAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
      </Typography>
      {keys.map(k => (
        <Box key={k} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{changeLabel(k)}</Typography>
          <Typography variant="caption" color="text.secondary">Before</Typography>
          {box(valueText((prev as Record<string, unknown>)[k]), 'before')}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Now</Typography>
          {box(valueText((cur as Record<string, unknown>)[k]), 'after')}
        </Box>
      ))}
    </Section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.7rem', mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Box>
  )
}

// Collapsible, read-only recap of an already-approved earlier step, so the mentor
// can check prior work for context while reviewing the current step. Collapsed by
// default to keep the review focused on the step under review.
function PriorContext({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Box sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Button
        fullWidth onClick={() => setOpen(o => !o)}
        sx={{ justifyContent: 'space-between', px: 2, py: 1.25, textTransform: 'none', color: 'text.primary', bgcolor: 'action.hover', borderRadius: 0 }}
        endIcon={<Typography variant="caption" color="text.secondary">{open ? 'Hide' : 'Show'}</Typography>}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Button>
      {open && <Box sx={{ p: 2 }}>{children}</Box>}
    </Box>
  )
}

// Read-only recap of the submitted Step 2 draft. The decision (approve / send
// back) lives in the shared "Your decision" panel, context-switched to Step 2 —
// keeping the review flow consistent with Step 1.
function Step2DraftPanel({ draft }: { draft: Step2DraftRow }) {
  const p = draft.draft_payload ?? {}
  const isEng = draft.track_committed === 'engineering_design'

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {isEng ? 'Problem statement draft' : 'Research question draft'}
      </Typography>

      {/* Variables (Sci) or problem dimensions (Eng) */}
      {!isEng && (
        <>
          <DraftRecapField label="Independent variable" value={p.iv} />
          <DraftRecapField label="Dependent variable" value={p.dv} />
          <DraftRecapList  label="Controlled variables" items={p.cv} />
        </>
      )}
      {isEng && (
        <>
          <DraftRecapField label="User + context" value={p.user_context} />
          <DraftRecapField label="Need / pain" value={p.need} />
          <DraftRecapList  label="Constraints" items={p.constraints} />
          <DraftRecapList  label="Success criteria" items={p.success_criteria} />
        </>
      )}

      {/* Question / problem statement */}
      <DraftRecapField
        label={isEng ? 'Draft problem statement' : 'Draft research question'}
        value={isEng ? p.problem_statement : p.question}
        primary
      />

      {/* Method approach */}
      <DraftRecapList  label="What they'll build / use" items={p.method_categories} />
      <DraftRecapField label="Method details" value={p.method} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
        Submitted {fmtDateTime(draft.updated_at)}. This is a draft — the student will refine to final after Step 3 background research.
      </Typography>
    </Paper>
  )
}

// Read-only recap of a submitted Step 3 (Background Research). Decision lives in
// the shared "Your decision" panel (reviewCtx='step3').
const STEP3_COMMENT_SECTIONS: { key: string; label: string }[] = [
  { key: 'introduction', label: 'Introduction — why it matters' },
  { key: 'background',   label: 'Background & scientific principles' },
  { key: 'literature',   label: 'Previous research & gaps' },
  { key: 'conclusion',   label: 'Conclusion & refined question' },
]

// Default send-back note for Step 3: the detailed feedback lives in the
// per-section comments, so the overall note just points the student there.
const STEP3_SENDBACK_DEFAULT =
  "I've left comments on each section above. Please read them, make the fixes, and resubmit your background research."

// Default send-back note for Step 4 — mirrors Step 3.
const STEP4_SENDBACK_DEFAULT =
  "I've left comments on each section above. Please read them, make the fixes, and resubmit your hypothesis."

// Default send-back note for Step 5 — mirrors Step 3/4.
const STEP5_SENDBACK_DEFAULT =
  "I've left comments on each section above. Please read them, make the fixes, and resubmit your experimental design."

// Step 5 review sections — the concept groups the student works in.
const STEP5_SCI_SECTIONS: { key: string; label: string }[] = [
  { key: 'design_type', label: 'Experimental design type' },
  { key: 'materials',   label: 'Materials' },
  { key: 'apparatus',   label: 'Apparatus & software' },
  { key: 'procedure',   label: 'Procedure' },
  { key: 'data_collection_method', label: 'Data collection method' },
  { key: 'analysis_plan', label: 'Data analysis plan' },
  { key: 'error_analysis', label: 'Error analysis & improvements' },
]
const STEP5_ENG_SECTIONS: { key: string; label: string }[] = [
  { key: 'design_description', label: 'Design description' },
  { key: 'materials',          label: 'Materials & tools' },
  { key: 'build_procedure',    label: 'Development & testing plan' },
  { key: 'test_plan',          label: 'Performance criteria & iteration ideas' },
]
// Write-Up sections — framing content, not part of the AI gate. Shared keys
// across both tracks (labels fork); target_user is engineering-only.
function step5WriteUpSections(isEng: boolean): { key: string; label: string }[] {
  return [
    { key: 'purpose_rq', label: isEng ? 'Problem statement & purpose' : 'Purpose & research question' },
    ...(isEng ? [{ key: 'target_user', label: 'Target user description' }] : []),
    { key: 'variables_hypothesis', label: isEng ? 'User needs & MVP' : 'Variables & hypothesis' },
    { key: 'introduction', label: 'Introduction' },
    { key: 'acknowledgement', label: 'Acknowledgement' },
    { key: 'references', label: 'References' },
  ]
}

// Step 4 review sections — the concept groups the student works in.
const STEP4_COMMENT_SECTIONS: { key: string; label: string }[] = [
  { key: 'question',   label: 'Research question' },
  { key: 'iv',         label: 'Independent variable' },
  { key: 'cv',         label: 'Controlled variables' },
  { key: 'dv',         label: 'Dependent variable(s)' },
  { key: 'hypothesis', label: 'Hypothesis & grounding' },
  { key: 'tool',       label: 'Measurement tool' },
]
const STEP4_ENG_SECTIONS: { key: string; label: string }[] = [
  { key: 'problem',    label: 'Problem' },
  { key: 'audience',   label: 'Users & needs / performance goal' },
  { key: 'prior_art',  label: 'Prior art & gaps' },
  { key: 'requirement',label: 'Design requirements' },
  { key: 'solution',   label: 'Solution / MVP' },
]

// Gate-check chip labels — the AI review's five gate_checks per track, keyed
// the same as the concept groups above (sci and eng keys never collide).
const STEP4_GATE_LABEL: Record<string, string> = {
  question: 'Research question', iv: 'Independent variable', cv: 'Controlled variables',
  dv: 'Dependent variable(s)', hypothesis: 'Hypothesis & grounding',
  problem: 'Problem', audience: 'Users & needs', prior_art: 'Prior art & gaps',
  requirement: 'Design requirements', solution: 'Solution / MVP',
}
// Step 5's own gate-check chip labels (sci and eng keys never collide).
const STEP5_GATE_LABEL: Record<string, string> = {
  design_type: 'Design type', materials: 'Materials', apparatus: 'Apparatus & software',
  procedure: 'Procedure', data_collection_method: 'Data collection', analysis_plan: 'Analysis plan',
  error_analysis: 'Error analysis',
  design_description: 'Design description', build_procedure: 'Development & testing plan',
  test_plan: 'Performance criteria & iteration ideas',
  // Write-Up (not part of gate_checks, but used for comment-label lookups) —
  // sci-track default labels; Step5ReviewPanel overrides these per-track via
  // step5WriteUpSections for the shared purpose_rq/variables_hypothesis keys.
  purpose_rq: 'Purpose & research question', target_user: 'Target user description',
  variables_hypothesis: 'Variables & hypothesis',
  introduction: 'Introduction', acknowledgement: 'Acknowledgement', references: 'References',
}

// Mentor AI review — types. Per-section output is a draft comment the mentor
// edits (first paragraph = the gaps, rest = how to close them).
type MentorReviewLevel = 'winning' | 'competitive' | 'developing' | 'early'
interface MentorSectionReview { key: string; comment?: string }
type JudgeReadiness = 'ready' | 'shaky' | 'not_ready'
interface JudgeQuestion { question: string; readiness: JudgeReadiness; note?: string }
interface MentorReview {
  overall?:         {
    level: MentorReviewLevel; summary?: string; top_fix?: string
    readiness_score?: number; rating?: string
    answerable?: 'yes' | 'almost' | 'no'
    gate_checks?: { id: string; status: 'present' | 'partial' | 'missing'; note?: string }[]
    question_drift?: string
    gate_met?: boolean       // every check present — nothing left to fix
  }
  sections?:        MentorSectionReview[]
  judge_questions?: JudgeQuestion[]
  // Absent once the gate is fully met: the step is complete, so the AI stops
  // commenting on the work and approval is the mentor's call. See the
  // edge-function side in _shared/review-verdict.ts.
  overall_comment?: string   // consolidated student-facing "required vs optional" message
  prep_questions?:  JudgeQuestion[]  // the judge questions they are NOT ready for
}
// The five criterion-1 gate checks (must the background answer the research question?).
const GATE_LABEL: Record<string, string> = {
  significance:     'Why it matters',
  prior_work:       'Prior work',
  principles:       'Principles',
  prior_experiment: 'A prior experiment',
  gap:              'Gap / unique',
}
const GATE_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  present: 'success', partial: 'warning', missing: 'error',
}
/* Shown when every gate check is present. The AI has stopped commenting on the
   work at that point, so this says what is actually left: a human decision,
   and the questions worth rehearsing before the interview. */
function GateMetNotice({ review }: { review?: MentorReview | null }) {
  if (!review?.overall?.gate_met) return null
  const prep = review.prep_questions ?? []
  return (
    <Alert severity="success" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: prep.length ? 0.5 : 0 }}>
        Every check is met — this step is complete.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        There is nothing left for the student to fix, so the AI has no further comment on the
        work. Approving is your call.
      </Typography>
      {prep.length > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Worth rehearsing before the interview — {prep.length} judge question{prep.length === 1 ? '' : 's'}{' '}
          they are not ready for, flagged below.
        </Typography>
      )}
    </Alert>
  )
}

const JUDGE_READINESS: Record<JudgeReadiness, { label: string; color: 'success' | 'warning' | 'error' }> = {
  ready:     { label: 'Ready',     color: 'success' },
  shaky:     { label: 'Shaky',     color: 'warning' },
  not_ready: { label: 'Not ready', color: 'error' },
}
const REVIEW_LEVEL: Record<MentorReviewLevel, { label: string; color: 'success' | 'info' | 'warning' | 'error' }> = {
  winning:     { label: 'Winning',     color: 'success' },
  competitive: { label: 'Competitive', color: 'info' },
  developing:  { label: 'Developing',  color: 'warning' },
  early:       { label: 'Early',       color: 'error' },
}

// One reviewable section: the student's content, then the mentor's comment box
// (which the AI review can pre-draft), plus a "Refine with AI" co-draft loop — the
// mentor steers the AI with short directions and inserts the polished result.
type CoDraftTurn = { role: 'mentor' | 'ai'; text: string }
function ReviewSection({ title, commentKey, comments, onChange, children, projectId, sourceText, readOnly }: {
  title: string
  commentKey: string
  comments: Record<string, string>
  onChange: (key: string, value: string) => void
  children: React.ReactNode
  projectId?: string
  sourceText?: string
  readOnly?: boolean
}) {
  const [preview, setPreview] = useState(false)
  const [cdOpen, setCdOpen]   = useState(false)
  const [directive, setDirective] = useState('')
  const [history, setHistory] = useState<CoDraftTurn[]>([])
  const [cdBusy, setCdBusy]   = useState(false)
  const [cdErr, setCdErr]     = useState<string | null>(null)
  const value = comments[commentKey] ?? ''
  const latestDraft = [...history].reverse().find(t => t.role === 'ai')?.text ?? value

  async function sendDirective() {
    const d = directive.trim()
    if (!d || cdBusy || !projectId) return
    setCdBusy(true); setCdErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: title, source_text: sourceText, current_comment: latestDraft, directive: d, history },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setDirective('')
    } catch (e: any) {
      setCdErr(e.message ?? 'Could not refine — please try again.')
    } finally {
      setCdBusy(false)
    }
  }

  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', display: 'block', mb: 1 }}>
        {title}
      </Typography>
      {children}
      {readOnly ? (
        value.trim() ? (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Your saved comment</Typography>
            <Markdown>{value}</Markdown>
          </Box>
        ) : null
      ) : (
      <>
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">Your comment on this section · <em>Markdown supported</em></Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {projectId && (
            <Button size="small" startIcon={<AutoFixHighIcon fontSize="small" />} onClick={() => setCdOpen(o => !o)}>
              {cdOpen ? 'Close' : 'Refine with AI'}
            </Button>
          )}
          {value.trim() && <Button size="small" onClick={() => setPreview(p => !p)}>{preview ? 'Edit' : 'Preview'}</Button>}
        </Box>
      </Box>
      {preview ? (
        <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Markdown>{value}</Markdown>
        </Box>
      ) : (
        <TextField
          value={value}
          onChange={e => onChange(commentKey, e.target.value)}
          fullWidth multiline minRows={5} maxRows={24} size="small"
          placeholder="Markdown supported — **bold**, lists, [links](url). The AI review can pre-draft this."
        />
      )}

      {cdOpen && projectId && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Steer the AI to refine this comment — it starts from the current draft{sourceText ? ' and the student’s section' : ''}. Give short directions; insert when you’re happy.
          </Typography>
          {history.map((t, i) => t.role === 'mentor' && (
            <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>↳ {t.text}</Typography>
          ))}
          {history.some(t => t.role === 'ai') && (
            <Box sx={{ mb: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'primary.light', bgcolor: 'background.paper' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>Latest AI draft</Typography>
              <Markdown>{latestDraft}</Markdown>
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => { onChange(commentKey, latestDraft); setPreview(false) }}>
                Use this comment
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth size="small" multiline minRows={1} maxRows={4}
              placeholder="e.g. keep it student-driven — don’t hand her the metrics; tighten the gap logic"
              value={directive} onChange={e => setDirective(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendDirective() }}
            />
            <Button variant="contained" size="small" disabled={cdBusy || !directive.trim()} onClick={sendDirective} sx={{ mt: 0.25 }}>
              {cdBusy ? <CircularProgress size={16} color="inherit" /> : 'Send'}
            </Button>
          </Box>
          {cdErr && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{cdErr}</Typography>}
        </Box>
      )}
      </>
      )}
    </Box>
  )
}

/* One hypothesis block on the review page.
   A multi-hypothesis Step 4 repeats H1 and H2 across three sections — DV,
   Hypothesis & grounding, Measurement tool — so a mentor has to re-associate
   them each time. The marker was a grey caption the same size and weight as
   the field labels beside it, with nothing separating one block from the next,
   so the two ran together. Same marker in every section now, and an accent
   rule down the side gives each block an edge to read against. */
function HypothesisGroup({ index, multi, children }: {
  index: number; multi: boolean; children: React.ReactNode
}) {
  if (!multi) return <Box sx={{ mb: 1 }}>{children}</Box>
  return (
    <Box sx={{ mb: 2.5, pl: 1.75, borderLeft: '3px solid', borderColor: 'primary.main' }}>
      <Chip
        size="small"
        label={`H${index + 1}`}
        color="primary"
        sx={{ height: 20, fontWeight: 800, letterSpacing: '0.04em', mb: 0.5 }}
      />
      {children}
    </Box>
  )
}

function Step4ReviewPanel({ row, projectId, onReload, onRegisterFlush, readOnly, onUseComment }: {
  row: Step4Row; projectId: string; onReload?: () => void
  onRegisterFlush?: (fn: (() => Promise<void>) | null) => void
  readOnly?: boolean
  onUseComment?: (text: string) => void
}) {
  const p = row.payload ?? {}
  const isEng = row.track === 'engineering_design'
  const isDataScience = row.study_mode === 'data_science'
  const blocks = p.blocks ?? []
  const reqs = p.requirements ?? []
  const ml = p.ml_prototype ?? {}
  const fb = p.ai_feedback ?? {}
  const multi = blocks.length > 1
  const hasTool = !isDataScience && blocks.some(b => b.needs_tool)
  const sections = isEng ? STEP4_ENG_SECTIONS : STEP4_COMMENT_SECTIONS
  // The comment sent on the previous send-back (snapshotted server-side), shown
  // read-only so the mentor can check what the revision was meant to address.
  const lastSent = readOnly ? null : (row.last_sent_review ?? null)
  const lastSentHasContent = !!lastSent && (!!lastSent.note?.trim()
    || Object.values(lastSent.section_comments ?? {}).some(v => (v ?? '').trim()))

  const [comments, setComments] = useState<Record<string, string>>(() => {
    const saved = row.mentor_section_comments ?? {}
    // If the saved comments are exactly the ones already sent last cycle (shown
    // read-only in the "last time you told the student" block), start the boxes
    // fresh so the auto-run drafts can fill them for the revised text.
    const sent = row.last_sent_review?.section_comments ?? null
    const base = sent && JSON.stringify(saved) === JSON.stringify(sent) ? {} : saved
    return Object.fromEntries(sections.map(s => [s.key, base?.[s.key] ?? '']))
  })
  const [savingComments, setSavingComments] = useState(false)
  const [commentsSaved,  setCommentsSaved]  = useState(false)
  const [commentsErr,    setCommentsErr]    = useState<string | null>(null)

  function updateComment(key: string, value: string) {
    setComments(prev => ({ ...prev, [key]: value }))
    setCommentsSaved(false)
  }
  // Expose a flush so Send-back/Approve persists the current section comments too.
  const commentsRef = useRef(comments)
  commentsRef.current = comments
  useEffect(() => {
    if (readOnly) return
    onRegisterFlush?.(async () => {
      const { data, error } = await supabase.functions.invoke('step4-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: commentsRef.current },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
    })
    return () => onRegisterFlush?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  async function saveComments() {
    setSavingComments(true); setCommentsErr(null); setCommentsSaved(false)
    try {
      const { data, error } = await supabase.functions.invoke('step4-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: comments },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setCommentsSaved(true)
      onReload?.()
    } catch (e: any) {
      setCommentsErr(e.message ?? 'Could not save comments — please try again.')
    } finally {
      setSavingComments(false)
    }
  }

  // Read-only field + the student's own AI verdict, for mentor context.
  const Field = ({ label, value }: { label: string; value?: string }) =>
    (value ?? '').trim() ? (
      <Box sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
      </Box>
    ) : null
  const AiNote = ({ k }: { k: string }) => {
    const f = fb[k]
    if (!f) return null
    const line = (label: string, d?: Step4Dim) => d?.note
      ? <Typography key={label} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <strong>{label}{d.state ? ` (${d.state})` : ''}:</strong> {d.note}
        </Typography>
      : null
    return <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>{line('AI · shape', f.shape)}{line('AI · anchor', f.anchor)}</Box>
  }

  // Seed from the review auto-run on submit (cached on the row) so the score
  // shows without a manual run; the button below can force a fresh re-run.
  const [aiReview,  setAiReview]  = useState<MentorReview | null>((row.ai_review as MentorReview | null) ?? null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiErr,     setAiErr]     = useState<string | null>(null)

  // Draft each section's comment into ONLY the empty boxes — never clobber a
  // comment the mentor has already typed or saved.
  function applyDrafts(review: MentorReview) {
    const drafts: Record<string, string> = {}
    for (const s of review.sections ?? []) {
      if (s.comment?.trim() && !(commentsRef.current[s.key] ?? '').trim()) drafts[s.key] = s.comment.trim()
    }
    if (Object.keys(drafts).length > 0) {
      setComments(prev => ({ ...prev, ...drafts }))
      setCommentsSaved(false)
    }
  }

  // On open, draft the auto-run review into the (empty) boxes for a fresh cycle.
  useEffect(() => {
    if (readOnly) return
    if (row.ai_review) applyDrafts(row.ai_review as MentorReview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function runAiReview() {
    setAiLoading(true); setAiErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('step4-mentor-ai-review', {
        body: { project_id: projectId },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      const review = data.review as MentorReview
      setAiReview(review)
      applyDrafts(review)
    } catch (e: any) {
      setAiErr(e.message ?? 'Could not run the AI review — please try again.')
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <Box>
      {row.status !== 'submitted' && (
        <Alert severity="info" sx={{ mb: 1.5 }}>Currently in draft with the student — comments are saved, but wait for a resubmit to approve.</Alert>
      )}
      {lastSentHasContent && lastSent && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            Last time you told the student{lastSent.at ? ` · ${fmtDateTime(lastSent.at)}` : ''}
          </Typography>
          {lastSent.note?.trim() && <Markdown>{lastSent.note}</Markdown>}
          {Object.entries(lastSent.section_comments ?? {})
            .filter(([, v]) => (v ?? '').trim())
            .map(([k, v]) => (
              <Box key={k} sx={{ mt: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize', color: 'text.secondary' }}>
                  {STEP4_GATE_LABEL[k] ?? k}
                </Typography>
                <Markdown>{v}</Markdown>
              </Box>
            ))}
        </Alert>
      )}

      {!readOnly && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Review each section</Typography>
        <Button size="small" variant="contained" onClick={runAiReview} disabled={aiLoading}>
          {aiLoading ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Reviewing…</> : (aiReview ? 'Re-run AI review' : 'AI review')}
        </Button>
      </Box>
      )}
      {!readOnly && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {row.ai_review_at
          ? <><strong>Auto-scored on submit · {fmtDateTime(row.ai_review_at)}.</strong> This reflects the current submission. </>
          : !aiReview && <>Auto-scoring this submission — if the score isn’t here yet it’s still running; click AI review to run it now. </>}
        AI review drafts a comment into each empty section box. Edit or overwrite, then Save. Each comment appears to the student under that section; saved separately from approve / send-back.
      </Typography>
      )}

      {aiErr && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAiErr(null)}>{aiErr}</Alert>}
      {aiReview?.overall && (
        <Alert severity={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
            {typeof aiReview.overall.readiness_score === 'number' && (
              <Chip size="small" color={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'}
                label={`AI readiness ${aiReview.overall.readiness_score}/10`} sx={{ fontWeight: 700 }} />
            )}
            <strong>
              {REVIEW_LEVEL[aiReview.overall.level]?.label ?? aiReview.overall.level}
              {aiReview.overall.rating ? ` · ${aiReview.overall.rating}` : ''}
            </strong>
          </Box>
          {aiReview.overall.answerable && (
            <Box sx={{ mt: 0.5 }}><strong>Ready for Step 5 (the gate):</strong> {aiReview.overall.answerable}</Box>
          )}
          {(aiReview.overall.gate_checks ?? []).length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {aiReview.overall.gate_checks!.map(c => (
                <Chip key={c.id} size="small" variant="outlined" color={GATE_COLOR[c.status] ?? 'default'}
                  label={`${STEP4_GATE_LABEL[c.id] ?? c.id}: ${c.status}`} title={c.note} />
              ))}
            </Box>
          )}
          {aiReview.overall.summary && <Box sx={{ mt: 0.75 }}>{aiReview.overall.summary}</Box>}
          {aiReview.overall.top_fix && <Box sx={{ mt: 0.5 }}><strong>Highest-leverage fix:</strong> {aiReview.overall.top_fix}</Box>}
          {aiReview.overall.question_drift?.trim() && (
            <Box sx={{ mt: 0.5 }}><strong>⚠ Drifted from Step 2:</strong> {aiReview.overall.question_drift}</Box>
          )}
        </Alert>
      )}

      {isEng && (<>
        <ReviewSection title="Problem" commentKey="problem" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.problem} readOnly={readOnly}>
          <Field label="Problem to solve" value={p.problem} /><AiNote k="problem" />
        </ReviewSection>
        <ReviewSection title={p.for_human_user === false ? 'Performance goal' : 'Users & needs'} commentKey="audience" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.for_human_user === false ? p.performance_goal : p.users_needs} readOnly={readOnly}>
          {p.for_human_user === false
            ? <Field label="Beneficiary & performance goal" value={p.performance_goal} />
            : <Field label="Users & needs" value={p.users_needs} />}
          <AiNote k="audience" />
        </ReviewSection>
        <ReviewSection title="Prior art & gaps" commentKey="prior_art" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.prior_art} readOnly={readOnly}>
          <Field label="Existing solutions & gaps" value={p.prior_art} /><AiNote k="prior_art" />
        </ReviewSection>
        <ReviewSection title="Design requirements" commentKey="requirement" comments={comments} onChange={updateComment}
          projectId={projectId} readOnly={readOnly}
          sourceText={reqs.map((r, i) => `Requirement ${i + 1}: ${r.requirement ?? ''} — criterion: ${r.criterion ?? ''}`).join('\n')}>
          {reqs.map((r, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              {reqs.length > 1 && <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Requirement {i + 1}</Typography>}
              <Field label="Requirement" value={r.requirement} />
              <Field label="Performance criterion" value={r.criterion} />
              <AiNote k={`requirement:${i}`} />
            </Box>
          ))}
        </ReviewSection>
        <ReviewSection title="Solution / MVP" commentKey="solution" comments={comments} onChange={updateComment}
          projectId={projectId} readOnly={readOnly}
          sourceText={[p.mvp, p.tools, p.solution].filter(Boolean).join('\n\n')}>
          <Field label="MVP" value={p.mvp} />
          <Field label="Tools / materials" value={p.tools} />
          <Field label="Solution" value={p.solution} />
          <AiNote k="solution" />
        </ReviewSection>
      </>)}

      {!isEng && (<>
      <ReviewSection title="Research question" commentKey="question" comments={comments} onChange={updateComment}
        projectId={projectId} sourceText={p.question} readOnly={readOnly}>
        <Field label="Question" value={p.question} /><AiNote k="question" />
      </ReviewSection>

      <ReviewSection title="Independent variable" commentKey="iv" comments={comments} onChange={updateComment}
        projectId={projectId} sourceText={p.iv} readOnly={readOnly}>
        <Field label="What you change & how" value={p.iv} /><AiNote k="iv" />
      </ReviewSection>

      <ReviewSection title="Controlled variables" commentKey="cv" comments={comments} onChange={updateComment}
        projectId={projectId} sourceText={p.cv} readOnly={readOnly}>
        <Field label="Held constant" value={p.cv} /><AiNote k="cv" />
      </ReviewSection>

      <ReviewSection title="Dependent variable(s)" commentKey="dv" comments={comments} onChange={updateComment}
        projectId={projectId} readOnly={readOnly}
        sourceText={blocks.map((b, i) => `${multi ? `H${i + 1}: ` : ''}DV: ${b.dv ?? ''} — measured by: ${b.dv_kind === 'derived' ? `${b.measured_quantities ?? ''} → ${b.formula ?? ''}` : (b.dv_measurement ?? '')}`).join('\n')}>
        {blocks.map((b, i) => (
          <HypothesisGroup key={i} index={i} multi={multi}>
            <Field label="Dependent variable" value={b.dv} />
            {b.dv_kind === 'derived'
              ? (<><Field label="Measures" value={b.measured_quantities} /><Field label="Formula" value={b.formula} /></>)
              : <Field label="Measurement" value={b.dv_measurement} />}
            {b.needs_tool && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Builds a measurement tool (see Measurement tool section).</Typography>}
            <AiNote k={`dv:${i}`} />
          </HypothesisGroup>
        ))}
        {isDataScience && (ml.dataset || ml.model_name || ml.package) && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
              Data-science apparatus — the model IS the measurement tool
            </Typography>
            <Field label="Dataset" value={ml.dataset} />
            <Field label="Model" value={ml.model_name} />
            <Field label="Package / library" value={ml.package} />
            {ml.colab_url && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                <a href={ml.colab_url} target="_blank" rel="noopener noreferrer">Code link</a>
              </Typography>
            )}
          </Box>
        )}
      </ReviewSection>

      <ReviewSection title="Hypothesis & grounding" commentKey="hypothesis" comments={comments} onChange={updateComment}
        projectId={projectId} readOnly={readOnly}
        sourceText={blocks.map((b, i) => `${multi ? `H${i + 1}: ` : ''}${b.hypothesis ?? ''} — because: ${b.rationale ?? ''}`).join('\n')}>
        {blocks.map((b, i) => (
          <HypothesisGroup key={i} index={i} multi={multi}>
            <Field label="Hypothesis" value={b.hypothesis} />
            <Field label="Rationale" value={b.rationale} />
            <Field label="Supporting background" value={b.supporting_background} />
            <AiNote k={`hypothesis:${i}`} />
          </HypothesisGroup>
        ))}
      </ReviewSection>

      {hasTool && (
        <ReviewSection title="Measurement tool" commentKey="tool" comments={comments} onChange={updateComment}
          projectId={projectId} readOnly={readOnly}
          sourceText={blocks.filter(b => b.needs_tool).map((b, i) => `${multi ? `H${i + 1}: ` : ''}${b.tool ?? ''}`).join('\n')}>
          {blocks.map((b, i) => b.needs_tool ? (
            <HypothesisGroup key={i} index={i} multi={multi}>
              <Field label="Tool to build/configure" value={b.tool} />
              <AiNote k={`tool:${i}`} />
            </HypothesisGroup>
          ) : null)}
        </ReviewSection>
      )}

      <GateMetNotice review={aiReview} />

      {(aiReview?.judge_questions ?? []).length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Judge Q&amp;A readiness</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Questions a fair judge is likely to ask — and whether the student's work equips them to answer. Use these to prep them for the interview.
          </Typography>
          {aiReview!.judge_questions!.map((q, i) => {
            const r = JUDGE_READINESS[q.readiness] ?? JUDGE_READINESS.shaky
            return (
              <Box key={i} sx={{ mb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip size="small" color={r.color} label={r.label} sx={{ mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.question}</Typography>
                </Box>
                {q.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, ml: 0.5 }}>{q.note}</Typography>}
              </Box>
            )
          })}
        </Box>
      )}
      </>)}

      {!readOnly && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" onClick={saveComments} disabled={savingComments}>
          {savingComments ? 'Saving…' : 'Save section comments'}
        </Button>
        {commentsSaved && <Typography variant="caption" color="success.main">Saved — the student will see these.</Typography>}
        {commentsErr && <Typography variant="caption" color="error.main">{commentsErr}</Typography>}
      </Box>
      )}

      {!readOnly && onUseComment && (
        <Step4FinalMessage
          projectId={projectId}
          initial={aiReview?.overall_comment ?? ''}
          sourceText={sections.map(s => (comments[s.key] ?? '').trim() ? `${s.label} — my note: ${comments[s.key].trim()}` : '').filter(Boolean).join('\n\n')}
          onUseComment={onUseComment}
        />
      )}
    </Box>
  )
}

// The overall message the student receives with a Step 4 review. Sits after the
// per-section comments: the mentor writes it directly, or steers the AI (same
// co-draft loop as Step 3) to draft it from the section notes, then inserts it
// into the "Your decision" box on the right.
function Step4FinalMessage({ projectId, sourceText, onUseComment, initial }: {
  projectId: string
  sourceText: string
  onUseComment: (text: string) => void
  initial?: string   // AI's consolidated "required vs optional" draft, if any
}) {
  const [message, setMessage]     = useState(initial ?? '')
  const [directive, setDirective] = useState('')
  const [history, setHistory]     = useState<CoDraftTurn[]>([])
  const [busy, setBusy]           = useState(false)
  const [err, setErr]             = useState<string | null>(null)
  const [used, setUsed]           = useState(false)

  // Seed from the AI's overall message once it arrives (auto-run cache or a
  // re-run) — but only into an untouched box, never over the mentor's edits.
  const touched = useRef(false)
  useEffect(() => {
    if (!touched.current && initial && !message.trim()) setMessage(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial])

  async function send() {
    const d = directive.trim()
    if (!d || busy) return
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: 'Step 4 — overall message to the student', source_text: sourceText, current_comment: message, directive: d, history },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setMessage(data.comment as string)
      setDirective(''); setUsed(false)
    } catch (e: any) {
      setErr(e.message ?? 'Could not draft — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Final message to the student</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The overall note the student receives with this review. Write it yourself, or steer the AI to draft it from your section comments above. <em>Markdown supported.</em> “Use as my comment” places it in your decision box on the right.
      </Typography>
      <TextField
        value={message} onChange={e => { touched.current = true; setMessage(e.target.value); setUsed(false) }}
        fullWidth multiline minRows={4} maxRows={20} size="small"
        placeholder="Overall message to the student — or give the AI a direction below to draft one."
      />
      <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
        {history.map((t, i) => t.role === 'mentor' && (
          <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>↳ {t.text}</Typography>
        ))}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth size="small" multiline minRows={1} maxRows={4}
            placeholder="e.g. summarize the two biggest fixes across sections, warm and encouraging, no jargon"
            value={directive} onChange={e => setDirective(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send() }}
          />
          <Button variant="contained" size="small" disabled={busy || !directive.trim()} onClick={send} sx={{ mt: 0.25 }}>
            {busy ? <CircularProgress size={16} color="inherit" /> : 'Draft'}
          </Button>
        </Box>
        {err && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{err}</Typography>}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" size="small" disabled={!message.trim()} onClick={() => { onUseComment(message); setUsed(true) }}>
          Use as my comment
        </Button>
        {used && <Typography variant="caption" color="success.main">Placed in your decision box →</Typography>}
      </Box>
    </Box>
  )
}

// ── Step 5 (Design Experiments / Develop Products) review panel ─────────────────
// v1: read-only recap of the student's work + per-section mentor comments (with
// the same "Refine with AI" co-draft loop as Steps 3/4) + a Final Message box.
// No cached AI review yet — step5-evaluate-field / step5-mentor-ai-review are a
// fast-follow (see memory step5-src-direction), so there's no score/gate-chips
// Alert block here, unlike Step3ReviewPanel/Step4ReviewPanel.
function Step5ReviewPanel({ row, projectId, onReload, onRegisterFlush, readOnly, onUseComment }: {
  row: Step5Row; projectId: string; onReload?: () => void
  onRegisterFlush?: (fn: (() => Promise<void>) | null) => void
  readOnly?: boolean
  onUseComment?: (text: string) => void
}) {
  const p = row.payload ?? {}
  const isEng = row.track === 'engineering_design'
  const designSections = isEng ? STEP5_ENG_SECTIONS : STEP5_SCI_SECTIONS
  const writeUpSecs = step5WriteUpSections(isEng)
  const sections = [...designSections, ...writeUpSecs]
  // Per-track label override for keys shared across both tracks
  // (purpose_rq/variables_hypothesis) — falls back to the static map below.
  const labelFor = (k: string) => sections.find(s => s.key === k)?.label ?? STEP5_GATE_LABEL[k] ?? k
  // The comment sent on the previous send-back (snapshotted server-side), shown
  // read-only so the mentor can check what the revision was meant to address.
  const lastSent = readOnly ? null : (row.last_sent_review ?? null)
  const lastSentHasContent = !!lastSent && (!!lastSent.note?.trim()
    || Object.values(lastSent.section_comments ?? {}).some(v => (v ?? '').trim()))

  const [comments, setComments] = useState<Record<string, string>>(() => {
    const saved = row.mentor_section_comments ?? {}
    const sent = row.last_sent_review?.section_comments ?? null
    const base = sent && JSON.stringify(saved) === JSON.stringify(sent) ? {} : saved
    return Object.fromEntries(sections.map(s => [s.key, base?.[s.key] ?? '']))
  })
  const [savingComments, setSavingComments] = useState(false)
  const [commentsSaved,  setCommentsSaved]  = useState(false)
  const [commentsErr,    setCommentsErr]    = useState<string | null>(null)

  function updateComment(key: string, value: string) {
    setComments(prev => ({ ...prev, [key]: value }))
    setCommentsSaved(false)
  }
  const commentsRef = useRef(comments)
  commentsRef.current = comments
  useEffect(() => {
    if (readOnly) return
    onRegisterFlush?.(async () => {
      const { data, error } = await supabase.functions.invoke('step5-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: commentsRef.current },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
    })
    return () => onRegisterFlush?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  async function saveComments() {
    setSavingComments(true); setCommentsErr(null); setCommentsSaved(false)
    try {
      const { data, error } = await supabase.functions.invoke('step5-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: comments },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setCommentsSaved(true)
      onReload?.()
    } catch (e: any) {
      setCommentsErr(e.message ?? 'Could not save comments — please try again.')
    } finally {
      setSavingComments(false)
    }
  }

  const Field = ({ label, value }: { label: string; value?: string }) =>
    (value ?? '').trim() ? (
      <Box sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
      </Box>
    ) : null

  // Seed from the review auto-run on submit (cached on the row) so the score
  // shows without a manual run; the button below can force a fresh re-run.
  const [aiReview,  setAiReview]  = useState<MentorReview | null>((row.ai_review as MentorReview | null) ?? null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiErr,     setAiErr]     = useState<string | null>(null)

  function applyDrafts(review: MentorReview) {
    const drafts: Record<string, string> = {}
    for (const s of review.sections ?? []) {
      if (s.comment?.trim() && !(commentsRef.current[s.key] ?? '').trim()) drafts[s.key] = s.comment.trim()
    }
    if (Object.keys(drafts).length > 0) {
      setComments(prev => ({ ...prev, ...drafts }))
      setCommentsSaved(false)
    }
  }

  useEffect(() => {
    if (readOnly) return
    if (row.ai_review) applyDrafts(row.ai_review as MentorReview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function runAiReview() {
    setAiLoading(true); setAiErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('step5-mentor-ai-review', {
        body: { project_id: projectId },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      const review = data.review as MentorReview
      setAiReview(review)
      applyDrafts(review)
    } catch (e: any) {
      setAiErr(e.message ?? 'Could not run the AI review — please try again.')
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <Box>
      {row.submission_note?.trim() && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>The student's note to you</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.submission_note.trim()}</Typography>
        </Alert>
      )}
      {row.status !== 'submitted' && (
        <Alert severity="info" sx={{ mb: 1.5 }}>Currently in draft with the student — comments are saved, but wait for a resubmit to approve.</Alert>
      )}
      {lastSentHasContent && lastSent && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            Last time you told the student{lastSent.at ? ` · ${fmtDateTime(lastSent.at)}` : ''}
          </Typography>
          {lastSent.note?.trim() && <Markdown>{lastSent.note}</Markdown>}
          {Object.entries(lastSent.section_comments ?? {})
            .filter(([, v]) => (v ?? '').trim())
            .map(([k, v]) => (
              <Box key={k} sx={{ mt: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize', color: 'text.secondary' }}>
                  {labelFor(k)}
                </Typography>
                <Markdown>{v}</Markdown>
              </Box>
            ))}
        </Alert>
      )}

      {!readOnly && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Review each section</Typography>
        <Button size="small" variant="contained" onClick={runAiReview} disabled={aiLoading}>
          {aiLoading ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Reviewing…</> : (aiReview ? 'Re-run AI review' : 'AI review')}
        </Button>
      </Box>
      )}
      {!readOnly && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {row.ai_review_at
          ? <><strong>Auto-scored on submit · {fmtDateTime(row.ai_review_at)}.</strong> This reflects the current submission. </>
          : !aiReview && <>Auto-scoring this submission — if the score isn’t here yet it’s still running; click AI review to run it now. </>}
        AI review drafts a comment into each empty section box. Edit or overwrite, then Save. Each comment appears to the student under that section; saved separately from approve / send-back.
      </Typography>
      )}

      {aiErr && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAiErr(null)}>{aiErr}</Alert>}
      {aiReview?.overall && (
        <Alert severity={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
            {typeof aiReview.overall.readiness_score === 'number' && (
              <Chip size="small" color={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'}
                label={`AI readiness ${aiReview.overall.readiness_score}/10`} sx={{ fontWeight: 700 }} />
            )}
            <strong>
              {REVIEW_LEVEL[aiReview.overall.level]?.label ?? aiReview.overall.level}
              {aiReview.overall.rating ? ` · ${aiReview.overall.rating}` : ''}
            </strong>
          </Box>
          {aiReview.overall.answerable && (
            <Box sx={{ mt: 0.5 }}><strong>Ready to build/run (the gate):</strong> {aiReview.overall.answerable}</Box>
          )}
          {(aiReview.overall.gate_checks ?? []).length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {aiReview.overall.gate_checks!.map(c => (
                <Chip key={c.id} size="small" variant="outlined" color={GATE_COLOR[c.status] ?? 'default'}
                  label={`${labelFor(c.id)}: ${c.status}`} title={c.note} />
              ))}
            </Box>
          )}
          {aiReview.overall.summary && <Box sx={{ mt: 0.75 }}>{aiReview.overall.summary}</Box>}
          {aiReview.overall.top_fix && <Box sx={{ mt: 0.5 }}><strong>Highest-leverage fix:</strong> {aiReview.overall.top_fix}</Box>}
        </Alert>
      )}

      {isEng ? (<>
        <ReviewSection title="Design description" commentKey="design_description" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.design_description} readOnly={readOnly}>
          <Field label="Design description" value={p.design_description} />
        </ReviewSection>
        <ReviewSection title="Materials & tools" commentKey="materials" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.materials} readOnly={readOnly}>
          <Field label="Materials & tools" value={p.materials} />
        </ReviewSection>
        <ReviewSection title="Development & testing plan" commentKey="build_procedure" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.build_procedure} readOnly={readOnly}>
          <Field label="Development & testing plan" value={p.build_procedure} />
        </ReviewSection>
        <ReviewSection title="Performance criteria & iteration ideas" commentKey="test_plan" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.test_plan} readOnly={readOnly}>
          <Field label="Performance criteria & iteration ideas" value={p.test_plan} />
        </ReviewSection>
      </>) : (<>
        <ReviewSection title="Experimental design type" commentKey="design_type" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.design_type} readOnly={readOnly}>
          <Field label="Design type" value={p.design_type} />
        </ReviewSection>
        <ReviewSection title="Materials" commentKey="materials" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.materials} readOnly={readOnly}>
          <Field label="Materials" value={p.materials} />
        </ReviewSection>
        <ReviewSection title="Apparatus & software" commentKey="apparatus" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.apparatus} readOnly={readOnly}>
          <Field label="Apparatus & software" value={p.apparatus} />
        </ReviewSection>
        <ReviewSection title="Procedure" commentKey="procedure" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.procedure} readOnly={readOnly}>
          <Field label="Procedure" value={p.procedure} />
        </ReviewSection>
        <ReviewSection title="Data collection & analysis" commentKey="data_collection_method" comments={comments} onChange={updateComment}
          projectId={projectId} readOnly={readOnly}
          sourceText={[p.data_collection_method, p.analysis_plan].filter(Boolean).join('\n\n')}>
          <Field label="Data collection method" value={p.data_collection_method} />
          {p.data_sheet_url && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Data table</Typography>
              <a href={p.data_sheet_url} target="_blank" rel="noopener noreferrer">{p.data_sheet_url}</a>
            </Box>
          )}
          <Field label="Analysis plan" value={p.analysis_plan} />
        </ReviewSection>
        <ReviewSection title="Error analysis & improvements" commentKey="error_analysis" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={p.error_analysis} readOnly={readOnly}>
          <Field label="Error analysis & improvements" value={p.error_analysis} />
        </ReviewSection>
      </>)}

      {/* Write-Up — framing content for the eventual Paper. Commentable like
          every other section, but NOT part of the AI gate_checks above (see
          _shared/step5-review.ts's scope note). */}
      {(p.purpose_rq || p.target_user || p.variables_hypothesis || p.introduction || p.acknowledgement || p.references) && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Write-Up</Typography>
          {writeUpSecs.map(s => (
            <ReviewSection key={s.key} title={s.label} commentKey={s.key} comments={comments} onChange={updateComment}
              projectId={projectId} sourceText={(p as any)[s.key]} readOnly={readOnly}>
              <Field label={s.label} value={(p as any)[s.key]} />
            </ReviewSection>
          ))}
        </>
      )}

      {p.paper_draft?.trim() && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary', mb: 0.5 }}>
            Draft Research Plan document (from the Paper tab)
          </Typography>
          <Box sx={{ maxHeight: 280, overflow: 'auto', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
              {p.paper_draft}
            </Typography>
          </Box>
        </Box>
      )}

      <GateMetNotice review={aiReview} />

      {(aiReview?.judge_questions ?? []).length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Judge Q&amp;A readiness</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Questions a fair judge is likely to ask — and whether the student's work equips them to answer. Use these to prep them for the interview.
          </Typography>
          {aiReview!.judge_questions!.map((q, i) => {
            const r = JUDGE_READINESS[q.readiness] ?? JUDGE_READINESS.shaky
            return (
              <Box key={i} sx={{ mb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip size="small" color={r.color} label={r.label} sx={{ mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.question}</Typography>
                </Box>
                {q.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, ml: 0.5 }}>{q.note}</Typography>}
              </Box>
            )
          })}
        </Box>
      )}

      {!readOnly && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <Button variant="outlined" size="small" onClick={saveComments} disabled={savingComments}>
          {savingComments ? 'Saving…' : 'Save section comments'}
        </Button>
        {commentsSaved && <Typography variant="caption" color="success.main">Saved — the student will see these.</Typography>}
        {commentsErr && <Typography variant="caption" color="error.main">{commentsErr}</Typography>}
      </Box>
      )}

      {!readOnly && onUseComment && (
        <Step5FinalMessage
          projectId={projectId}
          initial={aiReview?.overall_comment ?? ''}
          sourceText={sections.map(s => (comments[s.key] ?? '').trim() ? `${s.label} — my note: ${comments[s.key].trim()}` : '').filter(Boolean).join('\n\n')}
          onUseComment={onUseComment}
        />
      )}
    </Box>
  )
}

// The overall message the student receives with a Step 5 review. Mirrors
// Step3FinalMessage/Step4FinalMessage, including seeding from the AI review's
// consolidated overall_comment once it arrives.
function Step5FinalMessage({ projectId, sourceText, onUseComment, initial }: {
  projectId: string
  sourceText: string
  onUseComment: (text: string) => void
  initial?: string   // AI's consolidated "required vs optional" draft, if any
}) {
  const [message, setMessage]     = useState(initial ?? '')
  const [directive, setDirective] = useState('')
  const [history, setHistory]     = useState<CoDraftTurn[]>([])
  const [busy, setBusy]           = useState(false)
  const [err, setErr]             = useState<string | null>(null)
  const [used, setUsed]           = useState(false)

  // Seed from the AI's overall message once it arrives (auto-run cache or a
  // re-run) — but only into an untouched box, never over the mentor's edits.
  const touched = useRef(false)
  useEffect(() => {
    if (!touched.current && initial && !message.trim()) setMessage(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial])

  async function send() {
    const d = directive.trim()
    if (!d || busy) return
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: 'Step 5 — overall message to the student', source_text: sourceText, current_comment: message, directive: d, history },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setMessage(data.comment as string)
      setDirective(''); setUsed(false)
    } catch (e: any) {
      setErr(e.message ?? 'Could not draft — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Final message to the student</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The overall note the student receives with this review. Write it yourself, or steer the AI to draft it from your section comments above. <em>Markdown supported.</em> “Use as my comment” places it in your decision box on the right.
      </Typography>
      <TextField
        value={message} onChange={e => { touched.current = true; setMessage(e.target.value); setUsed(false) }}
        fullWidth multiline minRows={4} maxRows={20} size="small"
        placeholder="Overall message to the student — or give the AI a direction below to draft one."
      />
      <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
        {history.map((t, i) => t.role === 'mentor' && (
          <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>↳ {t.text}</Typography>
        ))}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth size="small" multiline minRows={1} maxRows={4}
            placeholder="e.g. summarize the two biggest fixes across sections, warm and encouraging, no jargon"
            value={directive} onChange={e => setDirective(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send() }}
          />
          <Button variant="contained" size="small" disabled={busy || !directive.trim()} onClick={send} sx={{ mt: 0.25 }}>
            {busy ? <CircularProgress size={16} color="inherit" /> : 'Draft'}
          </Button>
        </Box>
        {err && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{err}</Typography>}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" size="small" disabled={!message.trim()} onClick={() => { onUseComment(message); setUsed(true) }}>
          Use as my comment
        </Button>
        {used && <Typography variant="caption" color="success.main">Placed in your decision box →</Typography>}
      </Box>
    </Box>
  )
}

function Step3ReviewPanel({ row, projectId, onReload, onRegisterFlush, readOnly, onUseComment }: {
  row: Step3Row; projectId: string; onReload?: () => void
  onRegisterFlush?: (fn: (() => Promise<void>) | null) => void
  readOnly?: boolean
  onUseComment?: (text: string) => void
}) {
  const p = row.payload ?? {}
  const isEng = p.track === 'engineering_design'
  // The comment sent on the previous send-back (snapshotted server-side), shown
  // read-only so the mentor can check what the revision was meant to address.
  const lastSent = readOnly ? null : (row.last_sent_review ?? null)
  const lastSentHasContent = !!lastSent && (!!lastSent.note?.trim()
    || Object.values(lastSent.section_comments ?? {}).some(v => (v ?? '').trim()))
  const keywords = (p.keywords ?? []).map(k => (k ?? '').trim()).filter(Boolean)
  const legacySources = (p.sources ?? []).filter(s => (s.source ?? '').trim())
  // v1 → v2 read fallback so older submissions still render.
  const significance = p.significance ?? p.synthesis_background
  const whatShows    = p.what_research_shows ?? p.synthesis_literature

  const [comments, setComments] = useState<Record<string, string>>(() => {
    const saved = row.mentor_section_comments ?? {}
    // If the saved comments are exactly the ones already sent last cycle (shown
    // read-only in the "last time you told the student" block), start the boxes
    // fresh so the auto-run drafts can fill them for the revised text.
    const sent = row.last_sent_review?.section_comments ?? null
    const base = sent && JSON.stringify(saved) === JSON.stringify(sent) ? {} : saved
    return Object.fromEntries(STEP3_COMMENT_SECTIONS.map(s => [s.key, base?.[s.key] ?? '']))
  })
  const [savingComments, setSavingComments] = useState(false)
  const [commentsSaved,  setCommentsSaved]  = useState(false)
  const [commentsErr,    setCommentsErr]    = useState<string | null>(null)

  async function saveComments() {
    setSavingComments(true); setCommentsErr(null); setCommentsSaved(false)
    try {
      const { data, error } = await supabase.functions.invoke('step3-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: comments },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setCommentsSaved(true)
      onReload?.()
    } catch (e: any) {
      setCommentsErr(e.message ?? 'Could not save comments — please try again.')
    } finally {
      setSavingComments(false)
    }
  }

  function updateComment(key: string, value: string) {
    setComments(prev => ({ ...prev, [key]: value }))
    setCommentsSaved(false)
  }

  // Expose a flush so Send-back/Approve persists the current section comments too.
  const commentsRef = useRef(comments)
  commentsRef.current = comments
  useEffect(() => {
    if (readOnly) return
    onRegisterFlush?.(async () => {
      const { data, error } = await supabase.functions.invoke('step3-mentor-review', {
        body: { project_id: projectId, action: 'save_comments', section_comments: commentsRef.current },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
    })
    return () => onRegisterFlush?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Seed from the review auto-run on submit (cached on the row) so the score
  // shows without a manual run; the button below can force a fresh re-run.
  const [aiReview,  setAiReview]  = useState<MentorReview | null>((row.ai_review as MentorReview | null) ?? null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiErr,     setAiErr]     = useState<string | null>(null)

  // Draft each section's comment into ONLY the empty boxes — never clobber a
  // comment the mentor has already typed or saved.
  function applyDrafts(review: MentorReview) {
    const drafts: Record<string, string> = {}
    for (const s of review.sections ?? []) {
      if (s.comment?.trim() && !(commentsRef.current[s.key] ?? '').trim()) drafts[s.key] = s.comment.trim()
    }
    if (Object.keys(drafts).length > 0) {
      setComments(prev => ({ ...prev, ...drafts }))
      setCommentsSaved(false)
    }
  }

  // On open, draft the auto-run review into the (empty) boxes for a fresh cycle.
  useEffect(() => {
    if (readOnly) return
    if (row.ai_review) applyDrafts(row.ai_review as MentorReview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function runAiReview() {
    setAiLoading(true); setAiErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('step3-mentor-ai-review', {
        body: { project_id: projectId },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      const review = data.review as MentorReview
      setAiReview(review)
      applyDrafts(review)
    } catch (e: any) {
      setAiErr(e.message ?? 'Could not run the AI review — please try again.')
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      {row.submission_note?.trim() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>The student’s note to you</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.submission_note.trim()}</Typography>
        </Alert>
      )}
      {lastSentHasContent && lastSent && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            Last time you told the student{lastSent.at ? ` · ${fmtDateTime(lastSent.at)}` : ''}
          </Typography>
          {lastSent.note?.trim() && <Markdown>{lastSent.note}</Markdown>}
          {Object.entries(lastSent.section_comments ?? {})
            .filter(([, v]) => (v ?? '').trim())
            .map(([k, v]) => (
              <Box key={k} sx={{ mt: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize', color: 'text.secondary' }}>{k}</Typography>
                <Markdown>{v}</Markdown>
              </Box>
            ))}
        </Alert>
      )}
      {p.notebooklm_url && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary' }}>
            NotebookLM {p.notebook_access?.state === 'ok' ? '· access verified' : p.notebook_access?.state ? `· ${p.notebook_access.state}` : ''}
          </Typography>
          <a href={p.notebooklm_url} target="_blank" rel="noopener noreferrer">{p.notebooklm_url}</a>
        </Box>
      )}

      {keywords.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary' }}>Keywords</Typography>
          <Typography variant="body2">{keywords.join(' · ')}</Typography>
        </Box>
      )}

      <PaperRecap label="★ Review / meta-analysis paper" paper={p.review_paper} />
      <PaperRecap label="★ Model experimental paper" paper={p.model_paper} />

      {legacySources.length > 0 && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary', mb: 0.5 }}>
            Sources ({legacySources.length})
          </Typography>
          {legacySources.map((s, i) => (
            <Box key={i} sx={{ mb: 1, pl: 1.25, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.source}</Typography>
              {s.relevance && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Relevance: {s.relevance}</Typography>}
              {s.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>{s.note}</Typography>}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {!readOnly && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Review each section
          </Typography>
          <Button size="small" variant="contained" onClick={runAiReview} disabled={aiLoading}>
            {aiLoading ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Reviewing…</> : (aiReview ? 'Re-run AI review' : 'AI review')}
          </Button>
        </Box>
        )}
        {!readOnly && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {row.ai_review_at
            ? <><strong>Auto-scored on submit · {fmtDateTime(row.ai_review_at)}.</strong> This reflects the current submission. </>
            : !aiReview && <>Auto-scoring this submission — if the score isn’t here yet it’s still running; click AI review to run it now. </>}
          AI review drafts a comment into each empty section box. Edit or overwrite, then Save. Each comment appears to the student under that section; saved separately from approve / send-back.
        </Typography>
        )}

        {aiErr && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAiErr(null)}>{aiErr}</Alert>}
        {aiReview?.overall && (
          <Alert severity={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
              {typeof aiReview.overall.readiness_score === 'number' && (
                <Chip
                  size="small"
                  color={REVIEW_LEVEL[aiReview.overall.level]?.color ?? 'info'}
                  label={`AI readiness ${aiReview.overall.readiness_score}/10`}
                  sx={{ fontWeight: 700 }}
                />
              )}
              <strong>
                {REVIEW_LEVEL[aiReview.overall.level]?.label ?? aiReview.overall.level}
                {aiReview.overall.rating ? ` · ${aiReview.overall.rating}` : ''}
              </strong>
            </Box>
            {aiReview.overall.answerable && (
              <Box sx={{ mt: 0.5 }}>
                <strong>Answers its research question (the gate):</strong> {aiReview.overall.answerable}
              </Box>
            )}
            {(aiReview.overall.gate_checks ?? []).length > 0 && (
              <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {aiReview.overall.gate_checks!.map(c => (
                  <Chip
                    key={c.id} size="small" variant="outlined"
                    color={GATE_COLOR[c.status] ?? 'default'}
                    label={`${(isEng && c.id === 'prior_experiment' ? 'A current solution' : GATE_LABEL[c.id]) ?? c.id}: ${c.status}`}
                    title={c.note}
                  />
                ))}
              </Box>
            )}
            {aiReview.overall.summary && <Box sx={{ mt: 0.75 }}>{aiReview.overall.summary}</Box>}
            {aiReview.overall.top_fix && <Box sx={{ mt: 0.5 }}><strong>Highest-leverage fix:</strong> {aiReview.overall.top_fix}</Box>}
            {aiReview.overall.question_drift?.trim() && (
              <Box sx={{ mt: 0.5 }}><strong>⚠ Question drift from Step 2:</strong> {aiReview.overall.question_drift}</Box>
            )}
          </Alert>
        )}

        <ReviewSection title="1 · Introduction — why it matters" commentKey="introduction" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={significance} readOnly={readOnly}>
          <DraftRecapField label="Why this problem is important" value={significance} />
        </ReviewSection>

        <ReviewSection title="2 · Background & scientific principles" commentKey="background" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={[p.key_concepts, p.principles].filter(Boolean).join('\n\n')} readOnly={readOnly}>
          <DraftRecapField label="Background & key concepts" value={p.key_concepts} />
          <DraftRecapField label="Scientific principles" value={p.principles} />
        </ReviewSection>

        <ReviewSection title="3 · Previous research & gaps" commentKey="literature" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={[whatShows, p.the_gap].filter(Boolean).join('\n\n')} readOnly={readOnly}>
          <DraftRecapField label="What other researchers have found" value={whatShows} />
          <DraftRecapField label="Gaps & future directions" value={p.the_gap} />
        </ReviewSection>

        <ReviewSection title={`4 · Conclusion — ${isEng ? 'refined problem statement' : 'refined research question'}`} commentKey="conclusion" comments={comments} onChange={updateComment}
          projectId={projectId} sourceText={[p.conclusion, p.refined_question].filter(Boolean).join('\n\n')} readOnly={readOnly}>
          <DraftRecapField label="What they learned" value={p.conclusion} />
          <DraftRecapField label={isEng ? 'Refined problem statement' : 'Refined research question'} value={p.refined_question} primary />
          <DraftRecapField label="Purpose" value={p.purpose} />
        </ReviewSection>

        <GateMetNotice review={aiReview} />

      {(aiReview?.judge_questions ?? []).length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Judge Q&amp;A readiness</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Questions a fair judge is likely to ask — and whether the student's work equips them to answer. Use these to prep them for the interview.
            </Typography>
            {aiReview!.judge_questions!.map((q, i) => {
              const r = JUDGE_READINESS[q.readiness] ?? JUDGE_READINESS.shaky
              return (
                <Box key={i} sx={{ mb: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Chip size="small" color={r.color} label={r.label} sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.question}</Typography>
                  </Box>
                  {q.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, ml: 0.5 }}>{q.note}</Typography>}
                </Box>
              )
            })}
          </Box>
        )}
      </Box>

      {/* Feasibility moved to Step 4 — shown only for legacy submissions that captured it (no comment box) */}
      {(p.feasibility?.how_works || p.feasibility?.how_build || p.feasibility?.iv_levels || p.feasibility?.dv_measurement) && (
        <Box sx={{ mt: 1 }}>
          <DraftRecapField label="★ Feasibility — how it works" value={p.feasibility?.how_works} />
          <DraftRecapField label="★ Feasibility — how to make it" value={p.feasibility?.how_build} />
          <DraftRecapField label="★ Feasibility — set IV levels" value={p.feasibility?.iv_levels} />
          <DraftRecapField label="★ Feasibility — measure the DV" value={p.feasibility?.dv_measurement} />
        </Box>
      )}

      {p.paper_draft?.trim() && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary', mb: 0.5 }}>
            Draft paper (optional practice)
          </Typography>
          <Box sx={{ maxHeight: 280, overflow: 'auto', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
              {p.paper_draft}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Save all section comments (written inline above). */}
      {!readOnly && (
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={saveComments} disabled={savingComments}>
          {savingComments ? <><CircularProgress size={14} sx={{ mr: 1 }} />Saving…</> : 'Save section comments'}
        </Button>
        {commentsSaved && <Typography variant="caption" color="success.main">Saved — the student will see these.</Typography>}
        {commentsErr && <Alert severity="error" sx={{ py: 0 }} onClose={() => setCommentsErr(null)}>{commentsErr}</Alert>}
      </Box>
      )}

      {!readOnly && onUseComment && (
        <Step3FinalMessage
          projectId={projectId}
          initial={aiReview?.overall_comment ?? ''}
          sourceText={[
            p.refined_question ? `Refined ${isEng ? 'problem statement' : 'research question'}: ${p.refined_question}` : '',
            ...STEP3_COMMENT_SECTIONS.map(s => (comments[s.key] ?? '').trim() ? `${s.label} — my note: ${comments[s.key].trim()}` : ''),
          ].filter(Boolean).join('\n\n')}
          onUseComment={onUseComment}
        />
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
        Submitted {fmtDateTime(row.updated_at)}.
      </Typography>
    </Paper>
  )
}

// The overall message the student receives with a Step 3 review. Sits after the
// per-section comments: the mentor writes it directly, or steers the AI (same
// co-draft loop as Step 1) to draft it from the section notes, then inserts it
// into the "Your decision" box on the right.
function Step3FinalMessage({ projectId, sourceText, onUseComment, initial }: {
  projectId: string
  sourceText: string
  onUseComment: (text: string) => void
  initial?: string   // AI's consolidated "required vs optional" draft, if any
}) {
  const [message, setMessage]     = useState(initial ?? '')
  const [directive, setDirective] = useState('')
  const [history, setHistory]     = useState<CoDraftTurn[]>([])
  const [busy, setBusy]           = useState(false)
  const [err, setErr]             = useState<string | null>(null)
  const [used, setUsed]           = useState(false)

  // Seed from the AI's overall message once it arrives (auto-run cache or a
  // re-run) — but only into an untouched box, never over the mentor's edits.
  const touched = useRef(false)
  useEffect(() => {
    if (!touched.current && initial && !message.trim()) setMessage(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial])

  async function send() {
    const d = directive.trim()
    if (!d || busy) return
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: 'Step 3 — overall message to the student', source_text: sourceText, current_comment: message, directive: d, history },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setMessage(data.comment as string)
      setDirective(''); setUsed(false)
    } catch (e: any) {
      setErr(e.message ?? 'Could not draft — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Final message to the student</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The overall note the student receives with this review. Write it yourself, or steer the AI to draft it from your section comments above. <em>Markdown supported.</em> “Use as my comment” places it in your decision box on the right.
      </Typography>
      <TextField
        value={message} onChange={e => { touched.current = true; setMessage(e.target.value); setUsed(false) }}
        fullWidth multiline minRows={4} maxRows={20} size="small"
        placeholder="Overall message to the student — or give the AI a direction below to draft one."
      />
      <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
        {history.map((t, i) => t.role === 'mentor' && (
          <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>↳ {t.text}</Typography>
        ))}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth size="small" multiline minRows={1} maxRows={4}
            placeholder="e.g. summarize the two biggest fixes across sections, warm and encouraging, no jargon"
            value={directive} onChange={e => setDirective(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send() }}
          />
          <Button variant="contained" size="small" disabled={busy || !directive.trim()} onClick={send} sx={{ mt: 0.25 }}>
            {busy ? <CircularProgress size={16} color="inherit" /> : 'Draft'}
          </Button>
        </Box>
        {err && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{err}</Typography>}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="contained" size="small" disabled={!message.trim()} onClick={() => { onUseComment(message); setUsed(true) }}>
          Use as my comment
        </Button>
        {used && <Typography variant="caption" color="success.main">Placed in your decision box →</Typography>}
      </Box>
    </Box>
  )
}

function PaperRecap({ label, paper }: { label: string; paper?: Step3Paper }) {
  if (!paper || !(paper.url ?? paper.title ?? paper.abstract ?? '').trim()) return null
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary' }}>{label}</Typography>
      {paper.title && <Typography variant="body2" sx={{ fontWeight: 500 }}>{paper.title}</Typography>}
      {paper.url && <a href={paper.url} target="_blank" rel="noopener noreferrer">{paper.url}</a>}
      {paper.abstract && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>{paper.abstract}</Typography>}
    </Box>
  )
}

function DraftRecapField({
  label, value, primary,
}: { label: string; value: string | undefined; primary?: boolean }) {
  if (!value?.trim()) return null
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant={primary ? 'subtitle1' : 'body2'}
        sx={{ fontWeight: primary ? 600 : 400, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function DraftRecapList({
  label, items,
}: { label: string; items: string[] | undefined }) {
  const list = (items ?? []).filter(s => s?.trim())
  if (list.length === 0) return null
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary', mb: 0.25 }}>
        {label}
      </Typography>
      <Box component="ul" sx={{ pl: 2.25, m: 0 }}>
        {list.map((it, i) => (
          <li key={i}>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{it}</Typography>
          </li>
        ))}
      </Box>
    </Box>
  )
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function RevisionRequestRow({
  request, onDecided,
}: {
  request:    RevisionRequestRow
  onDecided:  () => void
}) {
  const [decision, setDecision] = useState<'approved' | 'denied' | null>(null)
  const [note,     setNote]     = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function respond(d: 'approved' | 'denied') {
    if (loading) return
    setDecision(d); setLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('revision-request-respond', {
        body: { request_id: request.id, decision: d, mentor_note: note.trim() || null },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      onDecided()
    } catch (e: any) {
      setError(e.message ?? 'Could not record your decision — please try again.')
      setDecision(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.light', bgcolor: 'rgba(237,108,2,0.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip size="small" color="warning" label={`Step ${request.step_number} revision`} />
        <Typography variant="caption" color="text.secondary">
          Requested {fmtDateTime(request.requested_at)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65, mb: 2 }}>
        {request.reason}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
        Approving reopens Step {request.step_number} for the student to edit in place, and
        reopens every later step as a draft with its content kept — nothing is deleted, and
        each step needs your approval again.
      </Typography>
      <TextField
        fullWidth multiline rows={2} size="small"
        label="Note to student (optional)"
        placeholder={decision === 'denied'
          ? 'Why you can\'t approve this — keep it constructive.'
          : 'Anything you want them to keep in mind as they redo this work.'}
        value={note}
        onChange={e => setNote(e.target.value)}
        disabled={loading}
        sx={{ mb: 1.5 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => respond('denied')} disabled={loading} color="inherit">
          {loading && decision === 'denied'
            ? <><CircularProgress size={14} sx={{ mr: 1 }} />Denying…</>
            : 'Deny'}
        </Button>
        <Button variant="contained" color="warning" onClick={() => respond('approved')} disabled={loading}>
          {loading && decision === 'approved'
            ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Approving…</>
            : 'Approve revision'}
        </Button>
      </Box>
    </Paper>
  )
}

function ActionRadio({
  label, desc, selected, onClick, disabled, primary,
}: {
  label: string; desc: string
  selected: boolean; onClick: () => void; disabled?: boolean; primary?: boolean
}) {
  const accent = primary ? 'success.main' : 'primary.main'
  return (
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        py: 1, pl: 1, pr: 1.25,
        display: 'flex', alignItems: 'flex-start', gap: 1.25,
        borderTop: '1px solid', borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
        bgcolor: selected
          ? (primary ? 'rgba(46,125,50,0.06)' : 'rgba(15,43,76,0.04)')
          : 'transparent',
        '&:hover': disabled ? {} : { bgcolor: 'rgba(15,43,76,0.03)' },
      }}
    >
      {/* Radio indicator */}
      <Box sx={{
        flexShrink: 0, mt: '3px',
        width: 16, height: 16, borderRadius: '50%',
        border: '2px solid',
        borderColor: selected ? accent : 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45, mt: 0.25 }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  )
}

interface Step1Review {
  overall?:                { summary?: string; stronger_direction?: string }
  finalists?:              { title: string; demonstration_risk?: 'low' | 'medium' | 'high'; assessment?: string; research_questions?: string[] }[]
  recommended_tutorials?:  { title: string; why?: string }[]
  draft_comment?:          string
}
const DEMO_RISK: Record<string, 'success' | 'warning' | 'error'> = { low: 'success', medium: 'warning', high: 'error' }

// Step 1 topic-review AI panel (mentor-only): judges the finalists (demonstration
// vs investigation), matches AI Lab tutorials to the student's level, and drafts a
// topic-selection-register comment the mentor can insert + refine.
function Step1AiReview({ projectId, onUseComment }: { projectId: string; onUseComment: (text: string) => void }) {
  const nav = useNavigate()
  const [loading, setLoading] = useState(false)
  const [review, setReview]   = useState<Step1Review | null>(null)
  const [err, setErr]         = useState<string | null>(null)
  const [directive, setDirective] = useState('')
  const [refining, setRefining]   = useState(false)
  const [cdHistory, setCdHistory] = useState<{ role: 'mentor' | 'ai'; text: string }[]>([])

  async function run() {
    setLoading(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('step1-mentor-ai-review', { body: { project_id: projectId } })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setReview(data.review as Step1Review)
      setCdHistory([])
    } catch (e: any) {
      setErr(e.message ?? 'Could not run the AI review — please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Steer the AI to refine the draft comment before using it (co-draft loop).
  async function refine() {
    const d = directive.trim()
    if (!d || refining || !review?.draft_comment) return
    setRefining(true); setErr(null)
    try {
      const sourceText = (review.finalists ?? []).map(f => `${f.title}: ${f.assessment ?? ''}`).join('\n')
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: 'Step 1 topic feedback', source_text: sourceText, current_comment: review.draft_comment, directive: d, history: cdHistory },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setCdHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setReview(r => (r ? { ...r, draft_comment: data.comment as string } : r))
      setDirective('')
    } catch (e: any) {
      setErr(e.message ?? 'Could not refine — please try again.')
    } finally {
      setRefining(false)
    }
  }

  if (!review) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          A judge’s-eye read of the finalists — is each a demonstration or an investigation? — with AI Lab tutorials matched to the student’s level (where a computational direction fits) and a draft comment you can edit and send.
        </Typography>
        <Button variant="contained" size="small" startIcon={<AutoFixHighIcon fontSize="small" />} onClick={run} disabled={loading}>
          {loading ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Reviewing…</> : 'Run AI review'}
        </Button>
        {err && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{err}</Typography>}
      </Box>
    )
  }

  return (
    <Box>
      {review.overall?.summary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">{review.overall.summary}</Typography>
          {review.overall.stronger_direction && (
            <Box sx={{ mt: 0.5 }}><strong>Stronger direction:</strong> {review.overall.stronger_direction}</Box>
          )}
        </Alert>
      )}

      {(review.finalists ?? []).map((f, i) => (
        <Box key={i} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{f.title}</Typography>
            {f.demonstration_risk && (
              <Chip size="small" variant="outlined" color={DEMO_RISK[f.demonstration_risk] ?? 'default'}
                label={`${f.demonstration_risk} demonstration risk`} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>{f.assessment}</Typography>
          {(f.research_questions ?? []).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                Research questions this could lead to
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {(f.research_questions ?? []).map((q, j) => (
                  <Typography key={j} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{q}</Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      ))}

      {(review.recommended_tutorials ?? []).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Suggested AI Lab tutorials
          </Typography>
          {(review.recommended_tutorials ?? []).map((t, i) => (
            <Box key={i} sx={{ mb: 0.75 }}>
              <Chip size="small" color="secondary" variant="outlined" clickable label={t.title}
                onClick={() => nav('/learn/ai-research-lab')} />
              {t.why && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{t.why}</Typography>}
            </Box>
          ))}
        </Box>
      )}

      {review.draft_comment && (
        <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>Draft comment for the student</Typography>
          <Markdown>{review.draft_comment}</Markdown>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth size="small" multiline minRows={1} maxRows={4}
              placeholder="Direct the AI to refine this — e.g. make it shorter and more direct; point to the diabetes tutorial; less praise"
              value={directive} onChange={e => setDirective(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) refine() }}
            />
            <Button size="small" variant="outlined" disabled={refining || !directive.trim()} onClick={refine} sx={{ mt: 0.25 }}>
              {refining ? <CircularProgress size={16} /> : 'Refine'}
            </Button>
          </Box>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" onClick={() => onUseComment(review.draft_comment!)}>Use as my comment</Button>
            <Button size="small" onClick={run} disabled={loading}>{loading ? 'Re-running…' : 'Re-run'}</Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

// Step 2 research-question AI review (mentor-only). Same shape as Step 1: a single
// assessment + one draft comment the mentor can refine and insert — NOT the per-section
// workspace of Step 3. Seeds from the review auto-run cached on submit (row.ai_review)
// so the score shows without waiting; the button forces a fresh re-run.
interface Step2Review {
  overall?: {
    answerable?:      'yes' | 'almost' | 'no'
    readiness_score?: number
    gate_checks?:     { id: string; status: 'present' | 'partial' | 'missing'; note?: string }[]
    question_drift?:  string
    summary?:         string
    top_fix?:         string
  }
  draft_comment?: string
}
const STEP2_GATE_LABEL: Record<string, string> = {
  rq_clarity:      'Question clear',
  iv_testable:     'IV testable',
  dv_measurable:   'DV measurable',
  cv_identified:   'Controls',
  method_feasible: 'Method feasible',
}
const ANSWERABLE: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  yes:    { label: 'Answerable',        color: 'success' },
  almost: { label: 'Almost answerable', color: 'warning' },
  no:     { label: 'Not yet answerable', color: 'error' },
}

function Step2AiReview({ row, projectId, onUseComment }: {
  row: Step2DraftRow; projectId: string; onUseComment: (text: string) => void
}) {
  const [review,  setReview]  = useState<Step2Review | null>((row.ai_review as Step2Review | null) ?? null)
  const [loading, setLoading] = useState(false)
  const [err,     setErr]     = useState<string | null>(null)
  const [directive, setDirective] = useState('')
  const [refining,  setRefining]  = useState(false)
  const [cdHistory, setCdHistory] = useState<{ role: 'mentor' | 'ai'; text: string }[]>([])

  async function run() {
    setLoading(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('step2-mentor-ai-review', { body: { project_id: projectId } })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setReview(data.review as Step2Review)
      setCdHistory([])
    } catch (e: any) {
      setErr(e.message ?? 'Could not run the AI review — please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Steer the AI to refine the draft comment before using it (co-draft loop).
  async function refine() {
    const d = directive.trim()
    if (!d || refining || !review?.draft_comment) return
    setRefining(true); setErr(null)
    try {
      const sourceText = [review.overall?.summary, review.overall?.top_fix].filter(Boolean).join('\n')
      const { data, error } = await supabase.functions.invoke('mentor-comment-codraft', {
        body: { project_id: projectId, section_label: 'Step 2 research-question feedback', source_text: sourceText, current_comment: review.draft_comment, directive: d, history: cdHistory },
      })
      if (error) throw new Error(await readFunctionError(error))
      if (data?.error) throw new Error(data.error)
      setCdHistory(h => [...h, { role: 'mentor', text: d }, { role: 'ai', text: data.comment as string }])
      setReview(r => (r ? { ...r, draft_comment: data.comment as string } : r))
      setDirective('')
    } catch (e: any) {
      setErr(e.message ?? 'Could not refine — please try again.')
    } finally {
      setRefining(false)
    }
  }

  const o = review?.overall
  const ans = o?.answerable ? ANSWERABLE[o.answerable] : null

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        A mentor’s read of the draft research question — is it answerable as written? — with a draft comment you can edit and send.{' '}
        {row.ai_review_at
          ? <><strong>Auto-scored on submit · {fmtDateTime(row.ai_review_at)}.</strong></>
          : !review && <>Auto-scoring this submission — if the score isn’t here yet it’s still running; click below to run it now.</>}
      </Typography>

      <Button variant={review ? 'text' : 'contained'} size="small" startIcon={<AutoFixHighIcon fontSize="small" />} onClick={run} disabled={loading} sx={{ mb: 2 }}>
        {loading ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Reviewing…</> : (review ? 'Re-run AI review' : 'Run AI review')}
      </Button>
      {err && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{err}</Typography>}

      {o && (
        <Alert severity={ans?.color ?? 'info'} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
            {typeof o.readiness_score === 'number' && (
              <Chip size="small" color={ans?.color ?? 'info'} label={`AI readiness ${o.readiness_score}/10`} sx={{ fontWeight: 700 }} />
            )}
            {ans && <strong>{ans.label}</strong>}
          </Box>
          {(o.gate_checks ?? []).length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {o.gate_checks!.map(c => (
                <Chip
                  key={c.id} size="small" variant="outlined"
                  color={GATE_COLOR[c.status] ?? 'default'}
                  label={`${STEP2_GATE_LABEL[c.id] ?? c.id}: ${c.status}`}
                  title={c.note}
                />
              ))}
            </Box>
          )}
          {o.summary && <Box sx={{ mt: 0.75 }}>{o.summary}</Box>}
          {o.top_fix && <Box sx={{ mt: 0.5 }}><strong>Highest-leverage fix:</strong> {o.top_fix}</Box>}
        </Alert>
      )}

      {review?.draft_comment && (
        <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.03)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>Draft comment for the student</Typography>
          <Markdown>{review.draft_comment}</Markdown>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth size="small" multiline minRows={1} maxRows={4}
              placeholder="Direct the AI to refine this — e.g. shorter and more direct; lead with what’s working; less praise"
              value={directive} onChange={e => setDirective(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) refine() }}
            />
            <Button size="small" variant="outlined" disabled={refining || !directive.trim()} onClick={refine} sx={{ mt: 0.25 }}>
              {refining ? <CircularProgress size={16} /> : 'Refine'}
            </Button>
          </Box>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" onClick={() => onUseComment(review.draft_comment!)}>Use as my comment</Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

function FinalistCard({ idx, topic, qa }: { idx: number; topic: AcceptedTopic; qa: QAEntry[] }) {
  const evalData = topic.evaluation as Record<string, any> | null
  const dims = evalData
    ? (['time_fit', 'budget', 'equipment', 'skills', 'specificity'] as const)
        .map(k => ({ key: k, d: evalData[k] }))
        .filter(x => x.d)
    : []
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          FINALIST {idx + 1}
        </Typography>
        {(topic.spanning_categories ?? []).filter(Boolean).map(c => (
          <Chip key={c} size="small" label={c} color="secondary" variant="outlined" />
        ))}
        {topic.source === 'student' && (
          <Chip size="small" label="Student-proposed" variant="outlined" color="primary" />
        )}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{topic.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        {topic.description}
      </Typography>

      {topic.finalist_thoughts && (
        <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(15,43,76,0.04)', mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.25 }}>
            Why this one
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {topic.finalist_thoughts}
          </Typography>
        </Box>
      )}

      {dims.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            AI feasibility read
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {dims.map(({ key, d }) => (
              <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Chip
                  size="small"
                  label={STATE_GLYPH(d.state ?? d.rating ?? 'ok')}
                  color={STATE_COLOR(d.state ?? d.rating ?? 'ok') as any}
                  sx={{ minWidth: 30, fontWeight: 700 }}
                />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {DIM_LABELS[key]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                    {d.note ?? d.explanation ?? ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {qa.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Student asked the AI ({qa.length})
          </Typography>
          {qa.map(q => (
            <Box key={q.id} sx={{ mb: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Q: {q.question}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.55 }}>
                A: {q.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}

// Step 6 — links to the student's own folders, their dated log, and what they
// actually did. The preliminary observation is what the mentor is approving.
function Step6ReviewPanel({ row }: { row: Step6Row }) {
  const p = row.payload ?? {}
  const links: Array<[string, string | undefined]> = [
    ['Data folder', p.data_folder_url], ['Photo folder', p.photo_folder_url], ['Code', p.code_url],
  ]
  const log = [...(p.log ?? [])].sort((a, b) => a.date.localeCompare(b.date))
  const block = (label: string, text?: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {text?.trim() || <em>Not filled in.</em>}
      </Typography>
    </Box>
  )
  return (
    <Box>
      <Box sx={{ mb: 2.5, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'success.light', bgcolor: 'rgba(46,125,50,0.04)' }}>
        <Typography variant="overline" color="success.main">Preliminary observation — what you are approving</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {p.preliminary_observation?.trim() || <em>Not filled in.</em>}
        </Typography>
      </Box>
      <Box sx={{ mb: 2 }}>
        {links.map(([label, url]) => (
          <Typography key={label} variant="body2" sx={{ mb: 0.5 }}>
            <strong>{label}:</strong>{' '}
            {url?.trim()
              ? <a href={url.trim()} target="_blank" rel="noopener noreferrer">{url.trim()}</a>
              : <em>none</em>}
          </Typography>
        ))}
        {p.data_folder_check && (
          <Typography variant="caption" color="text.secondary">Folder check: {p.data_folder_check.reason}</Typography>
        )}
      </Box>
      {block('Materials actually used', p.materials_actual)}
      {block('Procedure actually followed', p.procedure_actual)}
      <Typography variant="overline" color="text.secondary">Log ({log.length} {log.length === 1 ? 'entry' : 'entries'})</Typography>
      {log.length === 0 && <Typography variant="body2"><em>No entries.</em></Typography>}
      {log.map(e => (
        <Box key={e.id} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">{e.date}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{e.text}</Typography>
        </Box>
      ))}
    </Box>
  )
}
